<script setup lang="ts">
//https://vue-mapbox-gl.studiometa.dev/
//https://docs.mapbox.com/api/maps/styles/
import type MDBPrismeModèlesSource from '@/types/prisme/modèlessource'
import type MDBPrismeModèleHistorique from '@/types/prisme/modèlehistorique'
import type MDBPrismeOignonBotrytis from '@/types/prisme/oignonbotrytis'
import type MDBPrismeOignonMildiou from '@/types/prisme/oignonmildiou'
import type { MDBPrismeCaptureSpore } from '@/types/prisme/capturespore'
import { MapboxMap, MapboxLayer, MapboxMarker } from '@studiometa/vue-mapbox-gl';
import { roundTo, notifyError } from '@/utils/helpers'
import { useLocalStorage } from '@vueuse/core'
import type { ECOption } from '@/plugins/echarts'
import 'mapbox-gl/dist/mapbox-gl.css';



const storeU = useUtilisateurStore()
const route = useRoute()
const router = useRouter()
const theme = useTheme()
const display = useDisplay()
const isDark = computed(() => theme.global.current.value.dark)
const primaryColor = computed(() => theme.global.current.value.colors.primary)

// ── Theme-aware panel tokens ───────────────────────────────────────────
const panelBg           = computed(() => isDark.value ? 'rgba(18, 20, 30, 0.88)'       : 'rgba(255, 255, 255, 0.90)')
const panelShadow       = computed(() => isDark.value ? '0 4px 24px rgba(0,0,0,0.50)'  : '0 4px 24px rgba(0,0,0,0.15)')
const panelTextMain     = computed(() => isDark.value ? '#ffffff'                        : '#1a1a2e')
const panelTextSub      = computed(() => isDark.value ? 'rgba(255,255,255,0.50)'        : 'rgba(20,20,50,0.50)')
const panelTrackBg      = computed(() => isDark.value ? 'rgba(255,255,255,0.15)'        : 'rgba(0,0,0,0.12)')
const panelTickMinor    = computed(() => isDark.value ? 'rgba(255,255,255,0.22)'        : 'rgba(0,0,0,0.18)')
const panelTickMajor    = computed(() => isDark.value ? 'rgba(255,255,255,0.60)'        : 'rgba(0,0,0,0.50)')
const panelLabelMinor   = computed(() => isDark.value ? 'rgba(255,255,255,0.42)'        : 'rgba(0,0,0,0.38)')
const panelLabelMajor   = computed(() => isDark.value ? 'rgba(255,255,255,0.90)'        : 'rgba(0,0,0,0.78)')
const panelRangeFill    = computed(() => isDark.value ? 'rgba(107,170,255,0.38)'        : 'rgba(25,118,210,0.20)')
// ──────────────────────────────────────────────────────────────
const site = ref(storeU.siteSélectionné)
const selectedDay = ref(new Date(storeU.journéeSélectionnée))
const selectedHour = ref((() => {
  const now = new Date()
  const midnight = new Date(storeU.journéeSélectionnée)
  midnight.setHours(0, 0, 0, 0)
  return Math.round((now.getTime() - midnight.getTime()) / (1000 * 60 * 60))
})())
const selectedHourRange = ref<[number, number]>([12, 18])
const isRangeMode = ref(false)
const isLoading = ref(false)

const selectedDateTime = computed(() => {
  const date = new Date(selectedDay.value)
  const h = Math.floor(selectedHour.value)
  const m = Math.round((selectedHour.value - h) * 60)
  date.setHours(h, m, 0, 0)
  return date
})

const selectedDateTimeStart = computed(() => {
  const date = new Date(selectedDay.value)
  date.setHours(selectedHourRange.value[0])
  return date
})

const selectedDateTimeEnd = computed(() => {
  const date = new Date(selectedDay.value)
  date.setHours(selectedHourRange.value[1])
  return date
})

const selectedMetric = useLocalStorage<'temperature' | 'humidity' | 'precip' | 'lacy' | 'mildiou'>('carte.selectedMetric', 'temperature')
const showSpores = useLocalStorage('carte.showSpores', true)
const sporeCapteursData = ref<MDBPrismeCaptureCapteur[]>([])
const selectedSporeClients = useLocalStorage<string[]>('carte.selectedSporeClients', [])
const SPORE_CLIENT_NONE = '__AUCUN__'
const selectedSporeCulture = useLocalStorage('carte.selectedSporeCulture', '')

function normalizeSporeClient(clientName?: string | null): string {
  if (clientName && clientName.trim()) return clientName
  return SPORE_CLIENT_NONE
}

const sporeClientColorMap = computed(() => {
  const map = new Map<string, string>()
  const unique = [...new Set(sporeCapteursData.value.map(c => normalizeSporeClient(c.dernierClientNom)))]
  unique.forEach((client, i) => map.set(client, producerColors[i % producerColors.length]))
  return map
})

const sporeClientItems = computed(() =>
  [...sporeClientColorMap.value.entries()].map(([client, color]) => ({
    title: client === SPORE_CLIENT_NONE ? 'Aucun' : client,
    value: client,
    color,
  }))
)

const sporeClientsSummary = computed(() => {
  const total = sporeClientColorMap.value.size
  const sel = selectedSporeClients.value.length
  if (sel === 0) return 'Aucun'
  if (sel >= total) return 'Tous'
  return `${sel} / ${total}`
})

// Stable culture list built once during the full fetch; persisted so it survives navigation
const sporeCulturesStables = useLocalStorage<string[]>('carte.sporeCulturesStables', [])

const sporeCulturesDisponibles = computed(() => {
  const set = new Set<string>()
  sporeCapteursData.value.forEach(c => (c.cultures ?? []).forEach(cult => set.add(cult)))
  sporeCulturesStables.value.forEach(nom => set.add(nom))
  return [...set].sort((a, b) => a.localeCompare(b))
})

const filteredSporeCapteursData = computed(() =>
  sporeCapteursData.value.filter(c => {
    if (!c.dernièreLatitude || !c.dernièreLongitude) return false
    if (!selectedSporeClients.value.includes(normalizeSporeClient(c.dernierClientNom))) return false
    if (!selectedSporeCulture.value) return true
    const cultures = c.cultures ?? []
    return cultures.length === 0 || cultures.includes(selectedSporeCulture.value)
  })
)

// Split by state so each group is rendered in its own ordered v-for (DOM order = z-order)
const sporeCapteursNone     = computed(() => filteredSporeCapteursData.value.filter(c => (sporeCapteurStates.value.get(c.id) ?? 'none') === 'none'))
const sporeCapteursZero     = computed(() => filteredSporeCapteursData.value.filter(c => sporeCapteurStates.value.get(c.id) === 'zero'))
const sporeCapteursLow      = computed(() => filteredSporeCapteursData.value.filter(c => sporeCapteurStates.value.get(c.id) === 'low'))
const sporeCapteursPositive = computed(() => filteredSporeCapteursData.value.filter(c => sporeCapteurStates.value.get(c.id) === 'positive'))

// ── Spore captures (drive the marker icon/colour per sensor) ──────────────
const sporeCapturesData = ref<MDBPrismeCaptureSpore[]>([])

const SPORE_CULTURE_CODES: Record<string, string> = {
  'Oignon': 'ogn',
  'Laitue': 'lai',
  'Pomme de terre': 'pdt',
  'Vigne': 'vig',
  'Crucifère': 'cru',
}

// Icon + shared colour (icon, triangle and border) for each capture state
const SPORE_STATE_STYLE: Record<'default' | 'low' | 'positive' | 'zero' | 'none', { color: string, stroke: string, icon: string }> = {
  default: { color: '#E65100', stroke: '#7B2D00', icon: 'M12,2A10,10 0 0,1 22,12A2,2 0 0,1 20,14H4A2,2 0 0,1 2,12A10,10 0 0,1 12,2M12,8A2,2 0 0,0 14,6A2,2 0 0,0 12,4A2,2 0 0,0 10,6A2,2 0 0,0 12,8M17,12A2,2 0 0,0 19,10A2,2 0 0,0 17,8A2,2 0 0,0 15,10A2,2 0 0,0 17,12M7,12A2,2 0 0,0 9,10A2,2 0 0,0 7,8A2,2 0 0,0 5,10A2,2 0 0,0 7,12M15,15L16.27,19.45L16.35,20C16.35,21.1 15.45,22 14.35,22H9.65A2,2 0 0,1 7.65,20L7.73,19.45L9,15H15Z' },
  low:     { color: '#F9A825', stroke: '#A06800', icon: 'M12,2A10,10 0 0,1 22,12A2,2 0 0,1 20,14H4A2,2 0 0,1 2,12A10,10 0 0,1 12,2M12,8A2,2 0 0,0 14,6A2,2 0 0,0 12,4A2,2 0 0,0 10,6A2,2 0 0,0 12,8M17,12A2,2 0 0,0 19,10A2,2 0 0,0 17,8A2,2 0 0,0 15,10A2,2 0 0,0 17,12M7,12A2,2 0 0,0 9,10A2,2 0 0,0 7,8A2,2 0 0,0 5,10A2,2 0 0,0 7,12M15,15L16.27,19.45L16.35,20C16.35,21.1 15.45,22 14.35,22H9.65A2,2 0 0,1 7.65,20L7.73,19.45L9,15H15Z' },
  positive: { color: '#D32F2F', stroke: '#7F1414', icon: 'M12,2A10,10 0 0,1 22,12A2,2 0 0,1 20,14H4A2,2 0 0,1 2,12A10,10 0 0,1 12,2M12,8A2,2 0 0,0 14,6A2,2 0 0,0 12,4A2,2 0 0,0 10,6A2,2 0 0,0 12,8M17,12A2,2 0 0,0 19,10A2,2 0 0,0 17,8A2,2 0 0,0 15,10A2,2 0 0,0 17,12M7,12A2,2 0 0,0 9,10A2,2 0 0,0 7,8A2,2 0 0,0 5,10A2,2 0 0,0 7,12M15,15L16.27,19.45L16.35,20C16.35,21.1 15.45,22 14.35,22H9.65A2,2 0 0,1 7.65,20L7.73,19.45L9,15H15Z' },
  zero: { color: '#2E7D32', stroke: '#14431A', icon: 'M22.1 21.5L2.4 1.7L1.1 3L4.1 6C2.8 7.6 2 9.7 2 12C2 13.1 2.9 14 4 14H12.1L13.1 15H9L7.7 19.5V20C7.7 21.1 8.6 22 9.7 22H14.4C15.5 22 16.4 21.1 16.4 20L16.3 19.5L15.8 17.7L20.9 22.8L22.1 21.5M7 12C5.9 12 5 11.1 5 10C5 9.2 5.5 8.4 6.3 8.1L8.9 10.7C8.6 11.5 7.8 12 7 12M10 6C10 4.9 10.9 4 12 4S14 4.9 14 6 13.1 8 12 8C11.5 8 11.1 7.8 10.7 7.5L17.2 14H20C21.1 14 22 13.1 22 12C22 6.5 17.5 2 12 2C10.1 2 8.3 2.6 6.7 3.5L10.4 7.2C10.2 6.9 10 6.5 10 6M17 8C18.1 8 19 8.9 19 10S18.1 12 17 12 15 11.1 15 10 15.9 8 17 8Z' },
  none: { color: '#9E9E9E', stroke: '#5E5E5E', icon: 'M19,13H5V11H19V13Z' },
}

async function getCapturesSpores(forceCodes?: string[]) {
  sporeCapturesData.value = []
  // Explicit codes (bootstrap pass) > selected culture > all cultures
  const codes = forceCodes
    ?? (selectedSporeCulture.value
      ? [SPORE_CULTURE_CODES[selectedSporeCulture.value]].filter(Boolean)
      : [...new Set(Object.values(SPORE_CULTURE_CODES))])
  if (!codes.length) return
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    // Last 3 measurements = a 7-day window ending today
    const start = new Date()
    start.setHours(12, 0, 0, 0)
    start.setDate(start.getDate() - 6)
    const params = { date: start.toISOString(), nombreJours: 7 }
    const results = await Promise.allSettled(
      codes.map(code => apiProphet.get(`prsm/${siteCS}/captures/${code}`, { params })),
    )
    const data: MDBPrismeCaptureSpore[] = []
    for (const res of results) {
      if (res.status === 'fulfilled') data.push(...(res.value.data as MDBPrismeCaptureSpore[]))
    }
    sporeCapturesData.value = data
    // Always update stables from a full-codes fetch
    if (!selectedSporeCulture.value || forceCodes) {
      const noms = new Set<string>()
      data.forEach(r => { const nom = SPORE_CODE_CULTURE[r.codeCulture]; if (nom) noms.add(nom) })
      if (noms.size) sporeCulturesStables.value = [...noms]
    }
  }
  catch {
    sporeCapturesData.value = []
  }
}

watch(selectedSporeCulture, () => { getCapturesSpores() })

// capteurId -> state from the last 3 measurements (7-day window) of the selected culture(s)
const sporeCapteurStates = computed(() => {
  const map = new Map<string, 'positive' | 'zero' | 'none'>()
  const byCapteur = new Map<string, MDBPrismeCaptureSpore[]>()
  for (const r of sporeCapturesData.value) {
    const list = byCapteur.get(r.capteurId)
    if (list) list.push(r)
    else byCapteur.set(r.capteurId, [r])
  }
  for (const capteur of sporeCapteursData.value) {
    const recs = byCapteur.get(capteur.id)
    if (!recs || recs.length === 0) { map.set(capteur.id, 'none'); continue }
    const derniersJours = [...new Set(recs.map(r => r.date.substring(0, 10)))].sort().slice(-3)
    const joursRetenus = new Set(derniersJours)
    const mesures = recs.filter(r => joursRetenus.has(r.date.substring(0, 10)))
    const maxVal = Math.max(...mesures.map(r => r.valeur ?? 0))
    map.set(capteur.id, maxVal === 0 ? 'zero' : maxVal <= 5 ? 'low' : 'positive')
  }
  return map
})

function sporeCapteurStyle(capteur: MDBPrismeCaptureCapteur) {
  const state = sporeCapteurStates.value.get(capteur.id) ?? 'none'
  if (state === 'zero') {
    const primary = theme.global.current.value.colors.primary
    return { color: primary, stroke: primary, icon: SPORE_STATE_STYLE.zero.icon }
  }
  return SPORE_STATE_STYLE[state]
}

// Display names for the scientific disease names (mirrors captures-spores.vue)
const SPORE_MALADIE_DISPLAY: Record<string, string> = {
  'Stemphylium vesicarium': 'Stemphylium',
  'Peronospora destructor': 'Mildiou',
  'Botrytis squamosa': 'Botrytis',
  'Bremia lactucae': 'Mildiou',
  'Phytophtora infestans': 'Mildiou',
  'Alternaria solani': 'Alternaria solani',
  'Alternaria alternata': 'Alternaria alternata',
  'Botrytis cinerea': 'Pourriture grise',
  'Erysiphe necator': 'Oïdium',
  'Plasmopara viticola A': 'Mildiou A',
  'Plasmopara viticola B': 'Mildiou B',
  'Alternaria brassicicola': 'Alternariose brassicicola',
  'Alternaria brassicae': 'Alternariose brassicae',
}

function formatSporeDate(date: string) {
  return new Date(date).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })
}

// Reverse map: culture code -> display name (for "Toutes" mode)
const SPORE_CODE_CULTURE: Record<string, string> = Object.fromEntries(
  Object.entries(SPORE_CULTURE_CODES).map(([nom, code]) => [code, nom]),
)

// capteurId -> last-3-day measurements (most recent first) grouped by date, for the popup
const sporeCapteurMesures = computed(() => {
  const map = new Map<string, { date: string, items: { nom: string, culture: string, valeur: number }[] }[]>()
  const byCapteur = new Map<string, MDBPrismeCaptureSpore[]>()
  for (const r of sporeCapturesData.value) {
    const list = byCapteur.get(r.capteurId)
    if (list) list.push(r)
    else byCapteur.set(r.capteurId, [r])
  }
  for (const [capteurId, recs] of byCapteur) {
    const derniersJours = [...new Set(recs.map(r => r.date.substring(0, 10)))].sort().slice(-3).reverse()
    const groupes = derniersJours.map(jour => ({
      date: jour,
      items: recs
        .filter(r => r.date.substring(0, 10) === jour)
        .map(r => ({
          nom: SPORE_MALADIE_DISPLAY[r.nomMaladie] ?? r.nomMaladie,
          culture: SPORE_CODE_CULTURE[r.codeCulture] ?? r.codeCulture,
          valeur: r.valeur ?? 0,
        })),
    }))
    map.set(capteurId, groupes)
  }
  return map
})

const showChamps = useLocalStorage('carte.showChamps', true)
const showGéonotes = useLocalStorage('carte.showGéonotes', true)
const showStations = useLocalStorage('carte.showStations', true)
const showCapteurs = useLocalStorage('carte.showCapteurs', true)
const selectedProducers = ref<string[]>([])
const layerPanelGroups = useLocalStorage<string[]>('carte.layerPanelGroups', ['stations', 'spores', 'champs'])
const layerDrawerOpen = ref(false)

// ── Radar (RainViewer – free, no key required) ────────────────────────────
interface RadarFrame { time: number; path: string }
const showRadar = useLocalStorage('carte.showRadar', false)
const radarFrames = ref<RadarFrame[]>([])
const radarHost = ref('https://tilecache.rainviewer.com')
const radarOpacity = ref(0.7)
let radarRefreshInterval: ReturnType<typeof setInterval> | null = null

/** Radar frames converted to fractional hours from midnight of selectedDay, clamped to HOUR_MIN/MAX. */
const radarExtent = computed(() => {
  if (!radarFrames.value.length) return null
  const midnight = new Date(selectedDay.value)
  midnight.setHours(0, 0, 0, 0)
  const midnightMs = midnight.getTime()
  const hours = radarFrames.value.map(f => (f.time * 1000 - midnightMs) / 3_600_000)
  const rawMin = Math.min(...hours)
  const rawMax = Math.max(...hours)
  // Only usable when the radar window overlaps the selected day
  if (rawMax < HOUR_MIN || rawMin > HOUR_MAX) return null
  return {
    min: Math.max(HOUR_MIN, Math.floor(rawMin * 6) / 6),   // snap to 10-min boundary
    max: Math.min(HOUR_MAX, Math.ceil(rawMax  * 6) / 6)
  }
})
// ─────────────────────────────────────────────────────────────────────────

const layerItems = [
  { value: 'temperature', label: 'Température',   icon: 'M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z' },
  { value: 'humidity',    label: 'Humidité',       icon: 'M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z' },
  { value: 'precip',      label: 'Précipitations', icon: 'M6,14.03A1,1 0 0,1 7,15.03C7,15.58 6.55,16.03 6,16.03C3.24,16.03 1,13.79 1,11.03C1,8.27 3.24,6.03 6,6.03C7,3.68 9.3,2.03 12,2.03C15.43,2.03 18.24,4.69 18.5,8.06L19,8.03A4,4 0 0,1 23,12.03C23,14.23 21.21,16.03 19,16.03H18C17.45,16.03 17,15.58 17,15.03C17,14.47 17.45,14.03 18,14.03H19A2,2 0 0,0 21,12.03A2,2 0 0,0 19,10.03H17V9.03C17,6.27 14.76,4.03 12,4.03C9.5,4.03 7.45,5.84 7.06,8.21C6.73,8.09 6.37,8.03 6,8.03A3,3 0 0,0 3,11.03A3,3 0 0,0 6,14.03M12,14.15C12.18,14.39 12.37,14.66 12.56,14.94C13,15.56 14,17.03 14,18C14,19.11 13.1,20 12,20A2,2 0 0,1 10,18C10,17.03 11,15.56 11.44,14.94C11.63,14.66 11.82,14.4 12,14.15M12,11.03L11.5,11.59C11.5,11.59 10.65,12.55 9.79,13.81C8.93,15.06 8,16.56 8,18A4,4 0 0,0 12,22A4,4 0 0,0 16,18C16,16.56 15.07,15.06 14.21,13.81C13.35,12.55 12.5,11.59 12.5,11.59' },
] as const

// ── Layer group ACL ─────────────────────────────────────────────────────────
// Maps layer group key → required siteAccess string.
// Groups not listed here are visible to all authenticated users.
const layerGroupAccess: Record<string, string> = {
  spores: 'prisme.base',
}

function canSeeLayer(group: string): boolean {
  const access = layerGroupAccess[group]
  if (!access) return true
  return storeU.siteAAccès(access)
}

// ── Timeline ────────────────────────────────────────────────────────────────
const HOUR_MIN = -58
const HOUR_MAX = 94

// Animated view window – tweened toward radar extent (or full range)
const timelineMin = ref(HOUR_MIN)
const timelineMax = ref(HOUR_MAX)
let tweenRaf: number | null = null

function tweenTimeline(targetMin: number, targetMax: number, durationMs = 550) {
  if (tweenRaf !== null) cancelAnimationFrame(tweenRaf)
  const startMin = timelineMin.value
  const startMax = timelineMax.value
  const t0 = performance.now()
  function step(now: number) {
    const t = Math.min((now - t0) / durationMs, 1)
    const e = 1 - Math.pow(1 - t, 4) // ease-out-quart
    timelineMin.value = startMin + e * (targetMin - startMin)
    timelineMax.value = startMax + e * (targetMax - startMax)
    tweenRaf = t < 1 ? requestAnimationFrame(step) : null
  }
  tweenRaf = requestAnimationFrame(step)
}

const isPlaying = ref(false)
const showTimelineDatePicker = ref(false)
const isDragging = ref(false)
const dragTarget = ref<'single' | 'start' | 'end'>('single')
const timelineRef = ref<HTMLDivElement | null>(null)
let playInterval: ReturnType<typeof setInterval> | null = null

function hourToPct(h: number) {
  return ((h - timelineMin.value) / (timelineMax.value - timelineMin.value)) * 100
}
function pctToHour(pct: number) {
  const raw = timelineMin.value + (pct / 100) * (timelineMax.value - timelineMin.value)
  // In radar mode snap to nearest 10-min interval (1/6 h)
  return showRadar.value ? Math.round(raw * 6) / 6 : Math.round(raw)
}
function getHourFromPointer(e: PointerEvent) {
  if (!timelineRef.value) return selectedHour.value
  const rect = timelineRef.value.getBoundingClientRect()
  const pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100))
  return Math.max(timelineMin.value, Math.min(timelineMax.value, pctToHour(pct)))
}
function applyHour(h: number) {
  const clamped = Math.max(timelineMin.value, Math.min(timelineMax.value, h))
  if (!isRangeMode.value) {
    selectedHour.value = clamped
  } else if (dragTarget.value === 'start') {
    selectedHourRange.value = [Math.min(clamped, selectedHourRange.value[1] - 1), selectedHourRange.value[1]]
  } else {
    selectedHourRange.value = [selectedHourRange.value[0], Math.max(clamped, selectedHourRange.value[0] + 1)]
  }
}
function onTrackPointerDown(e: PointerEvent) {
  e.preventDefault()
  const h = getHourFromPointer(e)
  if (isRangeMode.value) {
    const distStart = Math.abs(h - selectedHourRange.value[0])
    const distEnd   = Math.abs(h - selectedHourRange.value[1])
    dragTarget.value = distStart <= distEnd ? 'start' : 'end'
  }
  isDragging.value = true
  applyHour(h)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup',   onPointerUp)
}
function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return
  applyHour(getHourFromPointer(e))
}
function onPointerUp() {
  isDragging.value = false
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup',   onPointerUp)
}
function startPlayInterval() {
  if (playInterval !== null) { clearInterval(playInterval); playInterval = null }
  const ext = radarExtent.value
  if (showRadar.value && ext) {
    // 10-min steps, 600 ms per frame → smooth animation matching tile updates
    playInterval = setInterval(() => {
      const next = Math.round((selectedHour.value + 1 / 6) * 6) / 6
      selectedHour.value = next > ext.max + 0.001 ? ext.min : next
    }, 600)
  } else {
    playInterval = setInterval(() => {
      selectedHour.value = selectedHour.value >= HOUR_MAX ? HOUR_MIN : selectedHour.value + 1
    }, 150)
  }
}
function togglePlay() {
  isPlaying.value = !isPlaying.value
  if (isPlaying.value) {
    startPlayInterval()
  } else {
    if (playInterval !== null) { clearInterval(playInterval); playInterval = null }
  }
}
function formatHourLabel(h: number) {
  const d = new Date(selectedDay.value)
  const hrs = Math.floor(h)
  const mins = Math.round((h - hrs) * 60)
  d.setHours(hrs, mins, 0, 0)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
function formatWeekday(date: Date) {
  return date.toLocaleDateString('fr-CA', { weekday: 'long' })
}
function formatDateLabel(date: Date) {
  return date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })
}

const nowHour = computed(() => {
  const now = new Date()
  const midnight = new Date(selectedDay.value)
  midnight.setHours(0, 0, 0, 0)
  const diffHours = (now.getTime() - midnight.getTime()) / (1000 * 60 * 60)
  if (diffHours < timelineMin.value || diffHours > timelineMax.value) return null
  return diffHours
})
const timelineTicks = computed(() => {
  const ticks = []
  const ext = showRadar.value && radarExtent.value
  if (ext) {
    // Radar mode: 10-min ticks, majors at every 30 min
    const step = 1 / 6
    const start = Math.ceil(timelineMin.value  * 6) / 6
    const end   = Math.floor(timelineMax.value * 6) / 6
    for (let h = start; h <= end + 0.001; h = Math.round((h + step) * 6) / 6) {
      const hrsInt  = Math.floor(h)
      const minsInt = Math.round((h - hrsInt) * 60)
      const d = new Date(selectedDay.value)
      d.setHours(hrsInt, minsInt, 0, 0)
      const isMajor = minsInt % 30 === 0
      const label   = minsInt % 30 === 0
        ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        : ''
      ticks.push({ h, pct: hourToPct(h), major: isMajor, label })
    }
  } else {
    // Full-range mode: 6-hour ticks
    const start = Math.ceil(timelineMin.value)
    const end   = Math.floor(timelineMax.value)
    for (let h = start; h <= end; h++) {
      const d = new Date(selectedDay.value)
      d.setHours(h, 0, 0, 0)
      const absHour = d.getHours()
      if (absHour % 6 === 0) {
        const isMidnight = absHour === 0
        ticks.push({
          h,
          pct: hourToPct(h),
          major: isMidnight,
          label: isMidnight
            ? d.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })
            : `${String(absHour).padStart(2, '0')}h`,
        })
      }
    }
  }
  return ticks
})
onUnmounted(() => {
  if (playInterval !== null) clearInterval(playInterval)
  if (radarRefreshInterval !== null) clearInterval(radarRefreshInterval)
  if (radarCleanupTimeout !== null) clearTimeout(radarCleanupTimeout)
  if (tweenRaf !== null) cancelAnimationFrame(tweenRaf)
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup',   onPointerUp)
})
// ────────────────────────────────────────────────────────────────────────────

// ── Radar functions ───────────────────────────────────────────────────────
async function fetchRadarFrames() {
  try {
    const res = await fetch('https://api.rainviewer.com/public/weather-maps.json')
    const data = await res.json()
    radarHost.value = data.host ?? 'https://tilecache.rainviewer.com'
    const past    = (data.radar?.past    ?? []) as RadarFrame[]
    const nowcast = (data.radar?.nowcast ?? []) as RadarFrame[]
    radarFrames.value = [...past, ...nowcast]
  } catch (e) {
    console.warn('[Radar] Failed to fetch frames', e)
  }
}

/** The available frame whose timestamp is closest to the selected time, or null if > 1 h away. */
const radarClosestFrame = computed(() => {
  if (radarFrames.value.length === 0) return null
  const targetTs = Math.floor(selectedDateTime.value.getTime() / 1000)
  let closest = radarFrames.value[0]
  let minDiff  = Math.abs(targetTs - closest.time)
  for (const frame of radarFrames.value) {
    const diff = Math.abs(targetTs - frame.time)
    if (diff < minDiff) { minDiff = diff; closest = frame }
  }
  return minDiff <= 3600 ? closest : null
})

/** Human-readable time label for the current radar frame (or null when out-of-range). */
const radarFrameLabel = computed(() => {
  const f = radarClosestFrame.value
  if (!f) return null
  const d = new Date(f.time * 1000)
  return d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
})

// ── Radar crossfade (ping-pong between two layer slots) ──────────────────
let radarActiveSlot = 0          // 0 = slot-a is showing, 1 = slot-b
let radarCleanupTimeout: ReturnType<typeof setTimeout> | null = null
const RADAR_FADE_MS = 500

function radarSlotIds(slot: number) {
  const s = slot === 0 ? 'a' : 'b'
  return { layerId: `radar-layer-${s}`, sourceId: `radar-tiles-${s}` }
}

function removeRadarSlot(map: any, slot: number) {
  const { layerId, sourceId } = radarSlotIds(slot)
  if (map.getLayer(layerId))  map.removeLayer(layerId)
  if (map.getSource(sourceId)) map.removeSource(sourceId)
}

/** Add / swap / remove the RainViewer raster layers with a smooth crossfade. */
function applyRadarLayer() {
  const map = mapRef.value?.map
  if (!map) return
  if (!map.isStyleLoaded()) { map.once('styledata', applyRadarLayer); return }

  if (!showRadar.value || !radarClosestFrame.value) {
    // Fade out both slots and remove
    for (const slot of [0, 1]) {
      const { layerId } = radarSlotIds(slot)
      if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'raster-opacity', 0)
    }
    if (radarCleanupTimeout !== null) clearTimeout(radarCleanupTimeout)
    radarCleanupTimeout = setTimeout(() => {
      removeRadarSlot(map, 0)
      removeRadarSlot(map, 1)
    }, RADAR_FADE_MS + 50)
    return
  }

  if (radarCleanupTimeout !== null) { clearTimeout(radarCleanupTimeout); radarCleanupTimeout = null }

  const nextSlot = 1 - radarActiveSlot
  const { layerId: nextLayerId, sourceId: nextSourceId } = radarSlotIds(nextSlot)
  const { layerId: prevLayerId } = radarSlotIds(radarActiveSlot)

  // Tile URL: color scheme 6 (NEXRAD-like), smooth=1, snow=1
  const tileUrl = `${radarHost.value}${radarClosestFrame.value.path}/512/{z}/{x}/{y}/6/1_1.png`

  // Insert below field polygons so semi-transparent fields appear on top
  const beforeLayer = map.getLayer('champs-polygons') ? 'champs-polygons' : undefined

  // Remove any lingering next-slot source/layer first (e.g., stale from rapid scrubbing)
  removeRadarSlot(map, nextSlot)

  map.addSource(nextSourceId, {
    type: 'raster',
    tiles: [tileUrl],
    tileSize: 512,
    // RainViewer radar tiles only go up to zoom level 7. Declaring maxzoom lets
    // Mapbox overzoom (scale up) the z7 tiles instead of requesting non-existent
    // higher-zoom tiles, which previously caused "zoom level not supported".
    maxzoom: 7,
    attribution: '© <a href="https://www.rainviewer.com" target="_blank">RainViewer</a>'
  })
  map.addLayer({
    id:   nextLayerId,
    type: 'raster',
    source: nextSourceId,
    paint: {
      'raster-opacity': 0,
      'raster-opacity-transition': { duration: RADAR_FADE_MS, delay: 0 },
      'raster-fade-duration': 0   // disable built-in tile fade so ours takes over
    }
  }, beforeLayer)

  // One rAF so Mapbox has a chance to register the layer before we transition it
  requestAnimationFrame(() => {
    if (map.getLayer(nextLayerId)) {
      map.setPaintProperty(nextLayerId, 'raster-opacity', radarOpacity.value)
    }
    if (map.getLayer(prevLayerId)) {
      map.setPaintProperty(prevLayerId, 'raster-opacity', 0)
    }
  })

  radarActiveSlot = nextSlot

  // Remove the outgoing slot once the fade completes
  const outgoing = 1 - nextSlot
  radarCleanupTimeout = setTimeout(() => {
    removeRadarSlot(map, outgoing)
    radarCleanupTimeout = null
  }, RADAR_FADE_MS + 50)
}

watch([showRadar, radarClosestFrame], applyRadarLayer)

// When radar is toggled or frames update: zoom/unzoom the timeline and restart play
watch([showRadar, radarExtent, selectedDay], () => {
  const ext = showRadar.value ? radarExtent.value : null
  if (ext) {
    tweenTimeline(ext.min, ext.max)
    // Snap selectedHour into the radar window if it isn't already
    if (selectedHour.value < ext.min - 0.001 || selectedHour.value > ext.max + 0.001) {
      // Park at the current time if it's inside, else at the middle frame
      const nowH = (() => {
        const midnight = new Date(selectedDay.value)
        midnight.setHours(0, 0, 0, 0)
        return (Date.now() - midnight.getTime()) / 3_600_000
      })()
      selectedHour.value = Math.max(ext.min, Math.min(ext.max,
        nowH >= ext.min && nowH <= ext.max ? Math.round(nowH * 6) / 6
        : (ext.min + ext.max) / 2
      ))
    }
  } else {
    tweenTimeline(HOUR_MIN, HOUR_MAX)
  }
  if (isPlaying.value) startPlayInterval()
}, { immediate: false })
// ─────────────────────────────────────────────────────────────────────────

const mapCenter = ref([site.value.longitudeGoogleMaps, site.value.latitudeGoogleMaps]);
const accessToken = ref('pk.eyJ1IjoiYWdyaW1ldHJpZSIsImEiOiJjbTc3cjhrMTgxZGNqMnFxOGM4aGpjaW5hIn0.fUMzok-3aNnM9Zg7hpBKjQ'); // public token
const mapRef = ref<InstanceType<typeof MapboxMap> | null>(null)

// ── GPS / User location ───────────────────────────────────────────────────
const userLocation = ref<[number, number] | null>(null)
const locating = ref(false)

function locateUser() {
  if (!navigator.geolocation) {
    notifyError('La géolocalisation n\'est pas supportée par ce navigateur.')
    return
  }
  locating.value = true
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      userLocation.value = [pos.coords.longitude, pos.coords.latitude]
      locating.value = false
      // Pan map to user location
      mapRef.value?.map?.flyTo({ center: userLocation.value, zoom: 12, speed: 1.4 })
    },
    (err) => {
      locating.value = false
      notifyError(`Impossible d'obtenir la position : ${err.message}`)
    },
    { enableHighAccuracy: true, timeout: 10000 }
  )
}

// ── Field notes ───────────────────────────────────────────────────────────────
const showNoteDialog = ref(false)
const noteSubmitting = ref(false)
const notePhotoPreview = ref<string | null>(null)
const noteForm = ref({
  date: new Date().toISOString().slice(0, 10),
  ferme: '',
  champ: '',
  note: '',
  photo: null as File | null,
})

/** Ray-casting point-in-polygon test for a single ring (array of [lng, lat] pairs). */
function pointInRing(point: [number, number], ring: number[][]): boolean {
  const [px, py] = point
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersect = ((yi > py) !== (yj > py)) &&
      (px < ((xj - xi) * (py - yi)) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

/** Returns the first GeoJSON field feature whose polygon contains [lng, lat]. */
function findFieldAtPoint(lng: number, lat: number): any | null {
  const pt: [number, number] = [lng, lat]
  for (const feature of champsData.value.features) {
    const geom = feature.geometry
    if (!geom) continue
    if (geom.type === 'Polygon') {
      if (pointInRing(pt, geom.coordinates[0])) return feature
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.coordinates) {
        if (pointInRing(pt, poly[0])) return feature
      }
    }
  }
  return null
}

function onNotePhotoChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0] ?? null
  noteForm.value.photo = file
  if (file) {
    const reader = new FileReader()
    reader.onload = (e) => { notePhotoPreview.value = e.target?.result as string }
    reader.readAsDataURL(file)
  } else {
    notePhotoPreview.value = null
  }
}

function fillNoteFieldFromCoords(lng: number, lat: number) {
  const field = findFieldAtPoint(lng, lat)
  noteForm.value.ferme = field?.properties?.fast_carto ?? ''
  noteForm.value.champ = field?.properties?.IDENCHAMP ?? ''
}

// Coordinates used when submitting the géonote (GPS position or map right-click)
const noteCoords = ref<[number, number] | null>(null)

interface SavedGéonote {
  id: number
  coords: [number, number]
  date: string
  ferme: string
  champ: string
  note: string
  photoPreview: string | null
}
let géonoteIdSeq = 0
const géonotesData = ref<SavedGéonote[]>([])

// Right-click context menu on the map
const mapContextMenu = ref({ show: false, x: 0, y: 0, lng: 0, lat: 0 })

function onMapContextMenu(e: any) {
  e.originalEvent?.preventDefault()
  mapContextMenu.value = {
    show: true,
    x: e.point?.x ?? 0,
    y: e.point?.y ?? 0,
    lng: e.lngLat?.lng ?? 0,
    lat: e.lngLat?.lat ?? 0,
  }
}

function openGeonoteFromContextMenu() {
  mapContextMenu.value.show = false
  const { lng, lat } = mapContextMenu.value
  noteCoords.value = [lng, lat]
  noteForm.value = {
    date: new Date().toISOString().slice(0, 10),
    ferme: '',
    champ: '',
    note: '',
    photo: null,
  }
  notePhotoPreview.value = null
  fillNoteFieldFromCoords(lng, lat)
  showNoteDialog.value = true
}

function openNoteDialog() {
  noteForm.value = {
    date: new Date().toISOString().slice(0, 10),
    ferme: '',
    champ: '',
    note: '',
    photo: null,
  }
  notePhotoPreview.value = null

  if (userLocation.value) {
    noteCoords.value = userLocation.value
    fillNoteFieldFromCoords(userLocation.value[0], userLocation.value[1])
    showNoteDialog.value = true
    return
  }

  if (!navigator.geolocation) {
    noteCoords.value = null
    showNoteDialog.value = true
    return
  }

  locating.value = true
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      userLocation.value = [pos.coords.longitude, pos.coords.latitude]
      noteCoords.value = userLocation.value
      locating.value = false
      mapRef.value?.map?.flyTo({ center: userLocation.value!, zoom: 14, speed: 1.4 })
      fillNoteFieldFromCoords(pos.coords.longitude, pos.coords.latitude)
      showNoteDialog.value = true
    },
    (err) => {
      locating.value = false
      noteCoords.value = null
      notifyError(`Impossible d'obtenir la position : ${err.message}`)
      showNoteDialog.value = true
    },
    { enableHighAccuracy: true, timeout: 10000 }
  )
}

async function submitNote() {
  const siteCS = storeU.siteSélectionné.lettresDappel
  const coords = noteCoords.value ?? userLocation.value

  // Always pin the géonote locally, regardless of API result
  if (coords) {
    géonotesData.value.push({
      id: ++géonoteIdSeq,
      coords: [coords[0], coords[1]],
      date: noteForm.value.date,
      ferme: noteForm.value.ferme,
      champ: noteForm.value.champ,
      note: noteForm.value.note,
      photoPreview: notePhotoPreview.value,
    })
  }
  showNoteDialog.value = false

  // Fire-and-forget POST (non-blocking)
  noteSubmitting.value = true
  try {
    const formData = new FormData()
    formData.append('date', noteForm.value.date)
    formData.append('ferme', noteForm.value.ferme)
    formData.append('champ', noteForm.value.champ)
    formData.append('note', noteForm.value.note)
    if (coords) {
      formData.append('longitude', String(coords[0]))
      formData.append('latitude', String(coords[1]))
    }
    if (noteForm.value.photo) {
      formData.append('photo', noteForm.value.photo)
    }
    await apiProphet.post(`prsm/${siteCS}/geonotes`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } catch {
    // POST not yet implemented – silently ignore
  } finally {
    noteSubmitting.value = false
  }
}
// ──────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────
const expandedMarkerId = ref<string | null>(null)

async function toggleMarker(id: string) {
  expandedMarkerId.value = expandedMarkerId.value === id ? null : id
  if (expandedMarkerId.value) {
    await nextTick()
    // The marker wrap lives inside a mapboxgl-marker div.
    // Moving that top-level Mapbox container to be the last sibling in the
    // markers overlay guarantees it paints above every other marker.
    const wrap = document.querySelector<HTMLElement>('.custom-marker-wrap--expanded, .spore-capteur-wrap--expanded')
    const markerEl = wrap?.closest<HTMLElement>('.mapboxgl-marker')
    if (markerEl?.parentElement) {
      markerEl.parentElement.appendChild(markerEl)
    }
  }
}

const sources = ref([] as MDBPrismeModèlesSource[])
const sourceSélectionnée = ref(null as MDBPrismeModèlesSource | null | undefined)
const sourcesData = ref({
  type: "FeatureCollection",
  features: []
} as any)

const champsData = ref({
  type: "FeatureCollection",
  features: []
} as any)

const hasChamps = computed(() => champsData.value.features.length > 0)

// Compute centroids for field labels as a GeoJSON FeatureCollection of points
const champsLabelsData = computed(() => {
  const features = champsData.value.features.map((feature: any) => {
    const coords = feature.geometry?.coordinates
    if (!coords || coords.length === 0) return null

    // Get the outer ring (first array for Polygon, or first polygon's first ring for MultiPolygon)
    const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
    if (!ring || ring.length === 0) return null

    // Calculate centroid
    let sumX = 0, sumY = 0
    for (const point of ring) {
      sumX += point[0]
      sumY += point[1]
    }
    const centroid = [sumX / ring.length, sumY / ring.length]

    const label = feature.properties?.IDENCHAMP || ''
    if (!label) return null

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: centroid
      },
      properties: {
        IDENCHAMP: label,
        fast_carto: feature.properties?.fast_carto ?? null
      }
    }
  }).filter((item: any) => item !== null)

  return {
    type: 'FeatureCollection',
    features
  }
})

// Compute one label point per fast_carto group (average centroid of all its polygons)
const champsGroupLabelsData = computed(() => {
  const groups = new Map<string, number[][]>() // fast_carto -> list of polygon centroids

  for (const feature of champsData.value.features) {
    const producer = feature.properties?.fast_carto
    if (!producer) continue

    const coords = feature.geometry?.coordinates
    if (!coords || coords.length === 0) continue

    const ring = feature.geometry.type === 'MultiPolygon' ? coords[0][0] : coords[0]
    if (!ring || ring.length === 0) continue

    let sumX = 0, sumY = 0
    for (const point of ring) {
      sumX += point[0]
      sumY += point[1]
    }
    const centroid = [sumX / ring.length, sumY / ring.length]

    if (!groups.has(producer)) groups.set(producer, [])
    groups.get(producer)!.push(centroid)
  }

  const features = [...groups.entries()].map(([producer, centroids]) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [
        centroids.reduce((s, c) => s + c[0], 0) / centroids.length,
        centroids.reduce((s, c) => s + c[1], 0) / centroids.length
      ]
    },
    properties: { fast_carto: producer }
  }))

  return { type: 'FeatureCollection', features }
})

declare interface MétéoData {
  id: string
  sourceId: string
  dateS: string
  température: number
  humidité: number
  totalLiquide: number
  lacy: number
  mildiou: number
  comblerSource: string
  comblerSourceId: string
  estPrévision: boolean
}
const météoData = ref([] as MétéoData[])

const dialogVisible = ref(false)
const selectedSource = ref<any>(null)

function openSourceDialog(feature: any) {
  selectedSource.value = feature
  dialogVisible.value = true
}

const selectedSourceMétéo = computed(() => {
  if (!selectedSource.value) return []
  return météoData.value
    .filter(m => m.sourceId === selectedSource.value.id)
    .sort((a, b) => new Date(a.dateS).getTime() - new Date(b.dateS).getTime())
})

const chartOptions = computed<ECOption>(() => {
  const data = selectedSourceMétéo.value
  const dates = data.map(d => {
    const date = new Date(d.dateS)
    return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}h`
  })
  const temperatures = data.map(d => roundTo(d.température, 1))
  const humidities = data.map(d => roundTo(d.humidité, 1))
  const precips = data.map(d => roundTo(d.totalLiquide, 2))
  const lacies = data.map(d => d.lacy >= 0 ? roundTo(d.lacy, 1) : null)
  const mildious = data.map(d => d.mildiou >= 0 ? roundTo(d.mildiou * 100, 1) : null)

  // Find the first forecast index for the marker line
  const firstForecastIndex = data.findIndex(d => d.estPrévision)
  const markLineData: any[] = []
  if (firstForecastIndex >= 0) {
    markLineData.push({
      xAxis: dates[firstForecastIndex],
      label: {
        formatter: 'Prévisions →',
        position: 'insideStartTop',
        fontSize: 11,
        color: '#E67E22'
      },
      lineStyle: { color: '#E67E22', type: 'solid', width: 2 }
    })
  }

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    legend: {
      data: ['Température', 'Humidité', 'Précipitations', 'Botrytis', 'Mildiou']
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dates,
      axisLabel: {
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: [
      {
        type: 'value',
        name: '°C / %',
        position: 'left',
        axisLine: { show: true },
        axisLabel: { formatter: '{value}' }
      },
      {
        type: 'value',
        name: 'mm',
        position: 'right',
        axisLine: { show: true },
        axisLabel: { formatter: '{value}' }
      }
    ],
    series: [
      {
        name: 'Température',
        type: 'line',
        data: temperatures,
        smooth: true,
        symbol: 'none',
        itemStyle: { color: '#EE6666' },
        lineStyle: { width: 2 },
        markLine: { data: markLineData, symbol: 'none' }
      },
      {
        name: 'Humidité',
        type: 'line',
        data: humidities,
        smooth: true,
        symbol: 'none',
        itemStyle: { color: '#5470C6' },
        lineStyle: { width: 2 }
      },
      {
        name: 'Précipitations',
        type: 'bar',
        yAxisIndex: 1,
        data: precips,
        itemStyle: { color: '#91CC75' }
      },
      {
        name: 'Botrytis',
        type: 'line',
        data: lacies,
        smooth: true,
        symbol: 'circle',
        symbolSize: 4,
        itemStyle: { color: '#FF9800' },
        lineStyle: { width: 2, type: 'dashed' },
        connectNulls: true
      },
      {
        name: 'Mildiou',
        type: 'line',
        data: mildious,
        smooth: true,
        symbol: 'diamond',
        symbolSize: 6,
        itemStyle: { color: '#9C27B0' },
        lineStyle: { width: 2, type: 'dotted' },
        connectNulls: true
      }
    ]
  }
})

onMounted(async () => {
   storeU.sélectionnerAnnée(new Date().getFullYear())
   selectedDay.value = new Date(storeU.journéeSélectionnée)
   await getInitialData()
   // Fly to coordinates from ?lat= ?lng= ?zoom= query params
   const qLat = parseFloat(route.query.lat as string)
   const qLng = parseFloat(route.query.lng as string)
   const qZoom = parseFloat(route.query.zoom as string) || 16
   if (route.query.layer === 'spores') showSpores.value = true
   if (!isNaN(qLat) && !isNaN(qLng)) {
     await nextTick()
     mapRef.value?.map?.flyTo({ center: [qLng, qLat], zoom: qZoom, speed: 1.4 })
   }
   // Close expanded marker on bare map click
   const rawMap = mapRef.value?.map
   if (rawMap) {
     rawMap.on('click', () => { expandedMarkerId.value = null })
   }
   // Radar: fetch frames now and refresh every 5 min
   await fetchRadarFrames()
   radarRefreshInterval = setInterval(fetchRadarFrames, 5 * 60 * 1000)
})
watch(() => storeU.siteSélectionné, async (_) => await getInitialData())
watch(selectedDay, async (newDay) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const picked = new Date(newDay.getFullYear(), newDay.getMonth(), newDay.getDate())
  const isCurrentYear = newDay.getFullYear() === now.getFullYear()
  const isPast = picked < today
  if (isCurrentYear && isPast) {
    selectedHour.value = 12
  }
  await getMétéoData()
})
watch(selectedDateTime, async (_) => { updateSourcesData() })
watch(selectedHourRange, async (_) => { updateSourcesData() })
watch(isRangeMode, () => { updateSourcesData() })
watch(showStations, (newVal) => {
  if (!newVal) expandedMarkerId.value = null
  if (newVal) showSpores.value = false
  if (!newVal && (selectedMetric.value === 'lacy' || selectedMetric.value === 'mildiou')) {
    selectedMetric.value = 'temperature'
  }
})

watch(showSpores, (newVal) => {
  if (newVal) showStations.value = false
})

watch(selectedMetric, (metric) => {
  if ((metric === 'lacy' || metric === 'mildiou') && !showStations.value) {
    showStations.value = true
  }
})

watch(showChamps, (newVal) => {
  const map = mapRef.value?.map
  if (!map) return

  const visibility = newVal ? 'visible' : 'none'
  if (map.getLayer('champs-polygons')) {
    map.setLayoutProperty('champs-polygons', 'visibility', visibility)
  }
  if (map.getLayer('champs-outlines')) {
    map.setLayoutProperty('champs-outlines', 'visibility', visibility)
  }
  if (map.getLayer('champs-labels')) {
    map.setLayoutProperty('champs-labels', 'visibility', visibility)
  }
  if (map.getLayer('champs-group-labels')) {
    map.setLayoutProperty('champs-group-labels', 'visibility', visibility)
  }
})

async function getInitialData() {
  await getModèlesSource()
  await getGeoJson()
  await getCapteurSpores()
}

async function getCapteurSpores() {
  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`prsm/${siteCS}/captures/capteurs`)
    sporeCapteursData.value = result.data as MDBPrismeCaptureCapteur[]
    const allClients = [...sporeClientColorMap.value.keys()]
    if (selectedSporeClients.value.length === 0) {
      // First load — select all
      selectedSporeClients.value = allClients
    } else {
      // Add any newly discovered clients without touching the existing selection
      const existing = new Set(selectedSporeClients.value)
      const newOnes = allClients.filter(c => !existing.has(c))
      if (newOnes.length) selectedSporeClients.value = [...selectedSporeClients.value, ...newOnes]
    }
    await getCapturesSpores()
    // If stables still empty after the fetch (e.g. first time with a culture pre-selected from
    // localStorage), do a one-shot full fetch so the culture filter list is always populated
    if (sporeCulturesStables.value.length === 0) {
      const allCodes = [...new Set(Object.values(SPORE_CULTURE_CODES))]
      await getCapturesSpores(allCodes)
      // Restore per-culture data if a culture is selected
      if (selectedSporeCulture.value) await getCapturesSpores()
    }
  }
  catch (error) {
    notifyError(error)
  }
}

async function getModèlesSource() {
  isLoading.value = true
  sources.value = []
  sourceSélectionnée.value = null
  sourcesData.value = {}

  console.log('Getting sources for site', storeU.siteSélectionné.lettresDappel)

  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`prsm/${siteCS}/sources`)
    sources.value = result.data
  }
  catch (error) {
    notifyError(error)
    return
  }

  if (sources.value.length > 0) {
    sourceSélectionnée.value = sources.value[0]
  }

  //console.log('sources', météoData.value)
  await getMétéoData()
  isLoading.value = false
}

async function getGeoJson() {
  console.log('Getting geojson for site', storeU.siteSélectionné.lettresDappel)
  isLoading.value = true

  try {
    const siteCS = storeU.siteSélectionné.lettresDappel
    const result = await apiProphet.get(`prsm/${siteCS}/champs/geojson`)
    const jsons = result.data as any[]
    console.log('geojson', jsons)

    // Combine all GeoJSON features into a single FeatureCollection
    const allFeatures: any[] = []
    for (const geojson of jsons) {
      if (geojson.type === 'FeatureCollection' && geojson.features) {
        allFeatures.push(...geojson.features)
      } else if (geojson.type === 'Feature') {
        allFeatures.push(geojson)
      }
    }

    champsData.value = {
      type: "FeatureCollection",
      features: allFeatures
    }

    // Reset producer filter – select all by default
    selectedProducers.value = [...producerColorMap.value.keys()]

    // Update the map sources and paint properties if already loaded
    const map = mapRef.value?.map
    if (map) {
      if (map.getSource('champs-polygons')) {
        (map.getSource('champs-polygons') as any).setData(champsData.value)
      }
      if (map.getSource('champs-outlines')) {
        (map.getSource('champs-outlines') as any).setData(champsData.value)
      }
      if (map.getSource('champs-labels')) {
        (map.getSource('champs-labels') as any).setData(champsLabelsData.value)
      }
      if (map.getSource('champs-group-labels')) {
        (map.getSource('champs-group-labels') as any).setData(champsGroupLabelsData.value)
      }

      // Repaint fill/outline colors now that fast_carto values are available
      if (map.getLayer('champs-polygons')) {
        map.setPaintProperty('champs-polygons', 'fill-color', buildFillColorExpression())
        map.setPaintProperty('champs-polygons', 'fill-outline-color', buildOutlineColorExpression())
      }
      if (map.getLayer('champs-outlines')) {
        map.setPaintProperty('champs-outlines', 'line-color', buildOutlineColorExpression())
      }
    }
  }
  catch (error) {
    const status = (error as any)?.response?.status
    const requestUrl = String((error as any)?.config?.url ?? '')
    const isChampsJson404 =
      status === 404
      && /prsm\/[^/]+\/champs\/(json|geojson)/i.test(requestUrl)

    if (isChampsJson404) {
      champsData.value = {
        type: "FeatureCollection",
        features: []
      }
      selectedProducers.value = []
    }

    if (!isChampsJson404) {
      notifyError(error)
    }
  }

  isLoading.value = false
}

// Incremented each time a new fetch is kicked off; lets in-flight calls detect they are stale.
let météoFetchGen = 0

async function getMétéoData() {
  const gen = ++météoFetchGen
  isLoading.value = true
  météoData.value = []

  const date = new Date(selectedDay.value)
  date.setDate(date.getDate() - 3)

  // Phase 1 – fetch base hourly météo for every source in parallel.
  await Promise.all(sources.value.map(source => getMétéo(date, source, gen)))
  if (gen !== météoFetchGen) return   // a newer call superseded us

  // Phase 2 – patch disease-model values in parallel (they annotate existing météoData entries).
  await Promise.all([
    ...sources.value.map(source => getBotrytis(date, source, gen)),
    ...sources.value.map(source => getMildiou(date, source, gen)),
  ])
  if (gen !== météoFetchGen) return   // stale

  const _now = new Date()
  const _today = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate())
  const _picked = new Date(selectedDay.value.getFullYear(), selectedDay.value.getMonth(), selectedDay.value.getDate())
  if (_picked.getTime() === _today.getTime()) {
    const _midnight = new Date(selectedDay.value)
    _midnight.setHours(0, 0, 0, 0)
    selectedHour.value = Math.round((_now.getTime() - _midnight.getTime()) / (1000 * 60 * 60))
  } else if (_picked < _today) {
    selectedHour.value = 12
  }
  updateSourcesData()
  isLoading.value = false
}

function updateSourcesData() {
  if (isRangeMode.value) {
    // Range mode: calculate mean values between start and end hours
    const startDateS = selectedDateTimeStart.value.toISOString().split('.')[0] + 'Z'
    const endDateS = selectedDateTimeEnd.value.toISOString().split('.')[0] + 'Z'

    sourcesData.value = {
      type: "FeatureCollection",
      features: sources.value
        .map((source) => {
          // Get all météo data in the range for this source
          const météosInRange = météoData.value.filter(m =>
            m.sourceId === source.id &&
            m.dateS >= startDateS &&
            m.dateS <= endDateS
          )

          if (météosInRange.length === 0) {
            return null
          }

          // Skip sources that haven't started yet
          if (new Date(météosInRange[0]?.dateS ?? "2000-01-01T00:00:00Z") < new Date(source.dateCommencement)) {
            return null
          }

          // Calculate mean, min, max values
          const temperatures = météosInRange.map(m => m.température)
          const humidities = météosInRange.map(m => m.humidité)
          const lacies = météosInRange.map(m => m.lacy)
          const mildious = météosInRange.map(m => m.mildiou)

          const avgTemp = temperatures.reduce((sum, t) => sum + t, 0) / temperatures.length
          const minTemp = Math.min(...temperatures)
          const maxTemp = Math.max(...temperatures)

          const avgHumidity = humidities.reduce((sum, h) => sum + h, 0) / humidities.length
          const minHumidity = Math.min(...humidities)
          const maxHumidity = Math.max(...humidities)

          // For precipitation, sum makes more sense than average
          const totalPrecip = météosInRange.reduce((sum, m) => sum + m.totalLiquide, 0)
          // For lacy, take the max value
          const maxLacy = Math.max(...lacies)
          // For mildiou, take the max value
          const maxMildiou = Math.max(...mildious)
          // If any data point is a forecast, mark as forecast
          const hasForecast = météosInRange.some(m => m.estPrévision)

          return {
            type: "Feature",
            id: source.id,
            geometry: {
              type: "Point",
              coordinates: [source.longitude, source.latitude]
            },
            properties: {
              name: source.nom,
              temperature: roundTo(avgTemp, 2),
              temperatureMin: roundTo(minTemp, 2),
              temperatureMax: roundTo(maxTemp, 2),
              humidity: roundTo(avgHumidity, 2),
              humidityMin: roundTo(minHumidity, 2),
              humidityMax: roundTo(maxHumidity, 2),
              precip: roundTo(totalPrecip, 2),
              lacy: roundTo(maxLacy, 2),
              mildiou: roundTo(maxMildiou, 2),
              prevision: hasForecast,
              isRange: true
            }
          }
        })
        .filter((feature): feature is NonNullable<typeof feature> => feature !== null)
    }
  } else {
    // Single hour mode – exact match first, then nearest entry within ±30 min as fallback
    // (needed in radar mode where selectedDateTime has sub-hour precision)
    const targetMs = selectedDateTime.value.getTime()
    const dateS = selectedDateTime.value.toISOString().split('.')[0] + 'Z'

    sourcesData.value = {
      type: "FeatureCollection",
      features: sources.value
        .map((source) => {
          let météo = météoData.value.find(m => m.sourceId === source.id && m.dateS === dateS)
          if (!météo) {
            // Nearest hourly entry within ±30 min
            const candidate = météoData.value
              .filter(m => String(m.sourceId) === String(source.id))
              .map(m => ({ m, diff: Math.abs(new Date(m.dateS).getTime() - targetMs) }))
              .sort((a, b) => a.diff - b.diff)[0]
            if (candidate && candidate.diff <= 30 * 60 * 1000) météo = candidate.m
          }

          // Skip sources that haven't started yet
          if (new Date(météo?.dateS ?? "2000-01-01T00:00:00Z") < new Date(source.dateCommencement)) {
            return null
          }

          return {
            type: "Feature",
            id: source.id,
            geometry: {
              type: "Point",
              coordinates: [source.longitude, source.latitude]
            },
            properties: {
              name: source.nom,
              temperature: météo ? roundTo(météo.température, 2) : -999,
              humidity: météo ? roundTo(météo.humidité, 2) : -999,
              precip: météo ? roundTo(météo.totalLiquide, 2) : -999,
              lacy: météo ? roundTo(météo.lacy, 2) : -1,
              mildiou: météo ? roundTo(météo.mildiou, 2) : -1,
              prevision: météo ? météo.estPrévision : false,
              isRange: false
            }
          }
        })
        .filter((feature): feature is NonNullable<typeof feature> => feature !== null)
    }
  }

  // Update the map source directly if map is loaded
  const map = mapRef.value?.map
  if (map && map.getSource('weather-stations-circles')) {
    (map.getSource('weather-stations-circles') as any).setData(sourcesData.value)
  }

  //console.log('sourcesData', sourcesData.value)
}

async function getMétéo(date: Date, source: MDBPrismeModèlesSource, gen: number) {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (!source) return

  try {
    const config = { params: { date: date, nombreJours: 7 } }
    const meteo = await apiProphet.get(`prsm/${siteCS}/historique/${source.id}/heure`, config)
    if (gen !== météoFetchGen) return   // stale – discard result
    const meteos = meteo.data as MDBPrismeModèleHistorique[]
    meteos.forEach(d => {
      météoData.value.push({
        id: d.id,
        sourceId: d.sourceId,
        dateS: d.date,
        température: d.température,
        humidité: d.humidité,
        totalLiquide: d.totalLiquide,
        lacy: -1,
        mildiou: -1,
        comblerSource: sources.value.find(s => s.id === d.comblerSourceId)?.nom || '',
        comblerSourceId: d.comblerSourceId,
        estPrévision: d.estPrévision
      })
    })
  } catch (error) {
    notifyError(error)
  }
}

async function getBotrytis(date: Date, source: MDBPrismeModèlesSource, gen: number) {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (!source) return

  try {
    const config = { params: { date: date, nombreJours: 7 } }
    const result = await apiProphet.get(`prsm/${siteCS}/ogn/botrytis/${source.id}`, config)
    if (gen !== météoFetchGen) return   // stale – discard result
    for (const d of result.data as MDBPrismeOignonBotrytis[]) {
      const meteoEntry = météoData.value.find(m => m.sourceId === d.sourceId && m.dateS === d.date)
      if (meteoEntry) meteoEntry.lacy = d.predictorLacy
    }
  } catch (error) {
    notifyError(error)
  }
}

async function getMildiou(date: Date, source: MDBPrismeModèlesSource, gen: number) {
  const siteCS = storeU.siteSélectionné.lettresDappel
  if (!source) return

  try {
    const config = { params: { date: date, nombreJours: 7 } }
    const result = await apiProphet.get(`prsm/${siteCS}/ogn/mildiou/${source.id}`, config)
    if (gen !== météoFetchGen) return   // stale – discard result
    for (const d of result.data as MDBPrismeOignonMildiou[]) {
      const dateOnly = d.date.split('T')[0]
      const meteoEntries = météoData.value.filter(m => m.sourceId === d.sourceId && m.dateS.split('T')[0] === dateOnly)
      for (const meteoEntry of meteoEntries) {
        meteoEntry.mildiou = d.risqueSporulation
      }
    }
  } catch (error) {
    notifyError(error)
  }
}

const producerColors = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
  '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FF5722',
  '#009688', '#673AB7', '#CDDC39', '#795548', '#607D8B',
  '#FFC107', '#03A9F4', '#76FF03', '#FF4081', '#536DFE'
]

const producerColorMap = computed(() => {
  const uniqueProducers = [...new Set(
    champsData.value.features
      .map((f: any) => f.properties?.fast_carto)
      .filter((v: any) => v != null)
  )] as string[]

  const map = new Map<string, string>()
  uniqueProducers.forEach((producer, i) => {
    map.set(producer, producerColors[i % producerColors.length])
  })
  return map
})

function buildFillColorExpression() {
  const entries = [...producerColorMap.value.entries()]
  if (entries.length === 0) return '#FFEB3B'
  const expr: any[] = ['match', ['get', 'fast_carto']]
  for (const [key, color] of entries) {
    expr.push(key, color)
  }
  expr.push('#FFEB3B') // fallback
  return expr
}

function buildOutlineColorExpression() {
  const entries = [...producerColorMap.value.entries()]
  if (entries.length === 0) return '#F57F17'
  const expr: any[] = ['match', ['get', 'fast_carto']]
  for (const [key, color] of entries) {
    // Darken by using same hue at lower brightness via a slightly darker shade
    expr.push(key, color)
  }
  expr.push('#F57F17') // fallback
  return expr
}

const metricConfigs = {
  temperature: {
    property: 'temperature',
    label: 'Température',
    unit: '°C',
    colorStops: [
      [-30, 'rgb(69,117,180)'],
      [-20, 'rgb(116,173,209)'],
      [-10, 'rgb(171,217,233)'],
      [0, 'rgb(224,243,248)'],
      [10, 'rgb(255,255,191)'],
      [20, 'rgb(254,224,144)'],
      [25, 'rgb(253,174,97)'],
      [30, 'rgb(244,109,67)'],
      [35, 'rgb(215,48,39)'],
      [40, 'rgb(165,0,38)']
    ]
  },
  humidity: {
    property: 'humidity',
    label: 'Humidité',
    unit: '%',
    colorStops: [
      [0, 'rgb(255,255,178)'],
      [20, 'rgb(254,217,118)'],
      [40, 'rgb(254,178,76)'],
      [60, 'rgb(253,141,60)'],
      [70, 'rgb(189,201,225)'],
      [80, 'rgb(116,169,207)'],
      [90, 'rgb(54,144,192)'],
      [100, 'rgb(5,112,176)']
    ]
  },
  precip: {
    property: 'precip',
    label: 'Précipitations',
    unit: 'mm',
    colorStops: [
      [0, 'rgb(255,255,255)'],
      [0.5, 'rgb(199,233,192)'],
      [2, 'rgb(161,217,155)'],
      [5, 'rgb(116,196,118)'],
      [10, 'rgb(65,171,93)'],
      [20, 'rgb(35,139,69)'],
      [30, 'rgb(0,109,44)'],
      [50, 'rgb(0,68,27)']
    ]
  },
  lacy: {
    property: 'lacy',
    label: 'Brûlure de la feuille (Botrytis)',
    unit: '%',
    colorStops: [
      [0, 'rgb(224,243,248)'],
      [20, 'rgb(255,255,191)'],
      [40, 'rgb(254,224,144)'],
      [50, 'rgb(253,174,97)'],
      [70, 'rgb(244,109,67)'],
      [85, 'rgb(215,48,39)'],
      [100, 'rgb(165,0,38)']
    ]
  },
  mildiou: {
    property: 'mildiou',
    label: 'Mildiou',
    unit: '',
    colorStops: [
      [-1, 'rgb(180,180,180)'],  // no data → grey
      [0,  'rgb(34,197,94)'],    // 0 → green
      [0.01, 'rgb(220,38,38)'], // any positive value → red
      [1,  'rgb(220,38,38)']
    ]
  },
}

const champsLayerOptions = computed(() => ({
  type: 'fill',
  source: {
    type: 'geojson',
    data: champsData.value,
  },
  layout: {
    visibility: showChamps.value ? 'visible' : 'none'
  },
  paint: {
    'fill-color': buildFillColorExpression(),
    'fill-opacity': 0.35,
    'fill-outline-color': buildOutlineColorExpression()
  }
}))

const champsOutlineLayerOptions = computed(() => ({
  type: 'line',
  source: {
    type: 'geojson',
    data: champsData.value,
  },
  layout: {
    visibility: showChamps.value ? 'visible' : 'none'
  },
  paint: {
    'line-color': buildOutlineColorExpression(),
    'line-width': 2
  }
}))

const champsLabelLayerOptions = computed(() => ({
  type: 'symbol',
  source: {
    type: 'geojson',
    data: champsLabelsData.value,
  },
  minzoom: 11,
  layout: {
    'text-field': ['get', 'IDENCHAMP'],
    'text-size': 12,
    'text-anchor': 'center',
    'text-allow-overlap': false,
    'text-ignore-placement': false,
    'text-optional': true,
    visibility: showChamps.value ? 'visible' : 'none'
  },
  paint: {
    'text-color': '#000000',
    'text-halo-color': '#FFFFFF',
    'text-halo-width': 1.5
  }
}))

const champsGroupLabelLayerOptions = computed(() => {
  // Build a match expression so each producer name is rendered in its own color
  const entries = [...producerColorMap.value.entries()]
  let textColor: any = '#FFFFFF'
  if (entries.length > 0) {
    textColor = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) {
      textColor.push(key, color)
    }
    textColor.push('#FFFFFF') // fallback
  }

  return {
    type: 'symbol',
    source: {
      type: 'geojson',
      data: champsGroupLabelsData.value,
    },
    layout: {
      'text-field': ['get', 'fast_carto'],
      'text-size': 15,
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-optional': true,
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-letter-spacing': 0.05,
      visibility: showChamps.value ? 'visible' : 'none'
    },
    paint: {
      'text-color': textColor,
      'text-halo-color': 'rgba(0, 0, 0, 0.75)',
      'text-halo-width': 2
    }
  }
})

// Highlight the field from ?champ= query param once GeoJSON and map are both ready
watch(champsData, async () => {
  const champ = route.query.champ as string | undefined
  if (!champ) return
  await nextTick()
  applyChampHighlight(champ)
})

// Also react when navigating to the map with ?champ= while it's already loaded
watch(() => route.query.champ, async (champ) => {
  if (!champ || !champsData.value.features.length) return
  await nextTick()
  applyChampHighlight(champ as string)
})

function applyChampHighlight(champ: string) {
  const feature = champsData.value.features.find(
    (f: any) => f.properties?.IDENCHAMP === champ
  )
  if (!feature) return

  const geom = feature.geometry
  let allPoints: number[][] = []
  if (geom.type === 'Polygon') {
    allPoints = geom.coordinates[0]
  } else if (geom.type === 'MultiPolygon') {
    for (const poly of geom.coordinates) allPoints.push(...poly[0])
  }
  if (allPoints.length === 0) return

  const minLng = Math.min(...allPoints.map((p: number[]) => p[0]))
  const maxLng = Math.max(...allPoints.map((p: number[]) => p[0]))
  const minLat = Math.min(...allPoints.map((p: number[]) => p[1]))
  const maxLat = Math.max(...allPoints.map((p: number[]) => p[1]))

  const map = mapRef.value?.map
  if (!map) return

  function addLayer() {
    if (map!.getLayer('champs-highlight')) map!.removeLayer('champs-highlight')
    if (!map!.getSource('champs-polygons')) return
    map!.addLayer({
      id: 'champs-highlight',
      type: 'line',
      source: 'champs-polygons',
      filter: ['==', ['get', 'IDENCHAMP'], champ],
      paint: { 'line-color': '#FFEB3B', 'line-width': 5, 'line-opacity': 1 }
    })
    map!.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 120, maxZoom: 16, duration: 1500 })
  }

  if (map.isStyleLoaded() && map.getSource('champs-polygons')) {
    addLayer()
  } else {
    map.once('idle', addLayer)
  }
}

/*
const circleLayerOptions = computed(() => {
  const config = metricConfigs[selectedMetric.value]

  const colorInterpolation: any[] = [
    'interpolate',
    ['linear'],
    ['get', config.property]
  ]
  config.colorStops.forEach(([value, color]) => {
    colorInterpolation.push(value, color)
  })

  return {
    type: 'circle',
    source: {
      type: 'geojson',
      data: sourcesData.value,
    },
    paint: {
      'circle-color': colorInterpolation,
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        6, 12,
        10, 25,
        14, 45,
        18, 70
      ],
      'circle-opacity': 0.8,
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 0
    },
  }
})
*/

function getMarkerText(properties: any): string {
  const config = metricConfigs[selectedMetric.value]
  const val = properties[config.property]

  if (selectedMetric.value === 'mildiou') {
    if (val < 0) return '—'
    return val >= 1 ? '⚠️' : '✓'
  }

  return val + config.unit
}

function interpolateMetricColor(value: number): string {
  const stops = metricConfigs[selectedMetric.value].colorStops
  const parseRgb = (s: string): [number, number, number] => {
    const m = s.match(/\d+/g)!
    return [+m[0], +m[1], +m[2]]
  }
  if (value <= (stops[0][0] as number)) return stops[0][1] as string
  if (value >= (stops[stops.length - 1][0] as number)) return stops[stops.length - 1][1] as string
  for (let i = 0; i < stops.length - 1; i++) {
    const v0 = stops[i][0] as number
    const c0 = stops[i][1] as string
    const v1 = stops[i + 1][0] as number
    const c1 = stops[i + 1][1] as string
    if (value >= v0 && value <= v1) {
      const t = (value - v0) / (v1 - v0)
      const [r0, g0, b0] = parseRgb(c0)
      const [r1, g1, b1] = parseRgb(c1)
      return `rgb(${Math.round(r0 + t * (r1 - r0))},${Math.round(g0 + t * (g1 - g0))},${Math.round(b0 + t * (b1 - b0))})`
    }
  }
  return stops[stops.length - 1][1] as string
}

function getMarkerAccentColor(properties: any): string {
  const config = metricConfigs[selectedMetric.value]
  const val = properties[config.property]
  if (selectedMetric.value === 'mildiou') {
    if (val < 0) return 'rgb(160,160,170)'
    return val >= 1 ? 'rgb(220,38,38)' : 'rgb(34,197,94)'
  }
  return interpolateMetricColor(val)
}

// Maps metricConfigs.property (English, for GeoJSON features) to the raw MétéoData field name
const metricToMétéoField: Record<string, keyof MétéoData> = {
  temperature: 'température',
  humidity:    'humidité',
  precip:      'totalLiquide',
  lacy:        'lacy',
  mildiou:     'mildiou',
}

function getSparklinePoints(sourceId: string): string {
  const field = metricToMétéoField[selectedMetric.value]
  if (!field) return ''
  const selectedMs = selectedDateTime.value.getTime()

  const rows = météoData.value
    .filter(m => String(m.sourceId) === String(sourceId))
    .sort((a, b) => new Date(a.dateS).getTime() - new Date(b.dateS).getTime())
    .filter(m => new Date(m.dateS).getTime() <= selectedMs)
    .slice(-24)

  if (rows.length < 2) return ''

  const rawValues = rows.map(m => m[field] as number)

  const W = 56, H = 18
  const valid = selectedMetric.value === 'mildiou'
    ? rawValues.filter(v => v >= 0)
    : rawValues

  if (valid.length < 2) return ''
  const min = Math.min(...valid)
  const max = Math.max(...valid)
  const range = max - min || 1

  return rawValues
    .map((v, i) => {
      const x = (i / (rawValues.length - 1)) * W
      const safeV = selectedMetric.value === 'mildiou' && v < 0 ? min : v
      const y = H - Math.max(0, ((safeV - min) / range)) * (H - 2) - 1
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

function getSparklineFill(sourceId: string): string {
  const pts = getSparklinePoints(sourceId)
  if (!pts) return ''
  const parts = pts.split(' ')
  if (parts.length < 2) return ''
  const last = parts[parts.length - 1].split(',')
  const first = parts[0].split(',')
  return `${pts} ${last[0]},18 ${first[0]},18 Z`
}

watch(selectedMetric, () => {
  const map = mapRef.value?.map
  if (map && map.getLayer('weather-stations-circles')) {
    const config = metricConfigs[selectedMetric.value]

    const colorInterpolation: any[] = [
      'interpolate',
      ['linear'],
      ['get', config.property]
    ]
    config.colorStops.forEach(([value, color]) => {
      colorInterpolation.push(value, color)
    })

    map.setPaintProperty('weather-stations-circles', 'circle-color', colorInterpolation)
  }
})

const champsFilterExpression = computed(() => {
  const all = [...producerColorMap.value.keys()]
  if (all.length === 0 || selectedProducers.value.length === all.length) return null
  if (selectedProducers.value.length === 0) return ['boolean', false]
  return ['in', ['get', 'fast_carto'], ['literal', selectedProducers.value]]
})

watch(selectedProducers, () => {
  const map = mapRef.value?.map
  if (!map) return
  const filter = champsFilterExpression.value
  for (const id of ['champs-polygons', 'champs-outlines', 'champs-labels', 'champs-group-labels']) {
    if (map.getLayer(id)) map.setFilter(id, filter as any)
  }
}, { deep: true })

/*const weatherStations = ref('https://static.dev.agrimetrie.ca/data/weather-stations.json');
//https://static.dev.agrimetrie.ca/data/pins.json
//https://static.dev.agrimetrie.ca/data/FHVW-geojson.json
//https://static.dev.agrimetrie.ca/data/notes-geojson.json
//https://static.dev.agrimetrie.ca/data/FHVW-greenhouses-4.json

const heatmapLayerOptions = ref({
      type: 'heatmap',
      source: {
        type: 'geojson',
        data: weatherStations.value,
      },
      paint: {
        'heatmap-weight': [
          'interpolate',
          ['linear'],
          ['get', 'precip'],
          0, 0,
          15, 1
        ],
        'heatmap-intensity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0, 1,
          10, 3
        ],
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0, 'rgba(33,102,172,0)',
          0.2, 'rgb(103,169,207)',
          0.4, 'rgb(209,229,240)',
          0.6, 'rgb(253,219,199)',
          0.8, 'rgb(239,138,98)',
          1, 'rgb(178,24,43)'
        ],
        'heatmap-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0, 1,
          16, 200
        ],
        'heatmap-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          7, 0.5,
          9, 0.5
        ],
      },
    });*/
</script>

<template>
  <div style="position: relative; width: 100%; height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px)); overflow: hidden;">
    <MapboxMap
      ref="mapRef"
      style="width: 100%; height: 100%;"
      :access-token="accessToken"
      map-style="mapbox://styles/mapbox/satellite-streets-v12"
      :center="mapCenter"
      :zoom="10"
      @mb-contextmenu="onMapContextMenu">

            <MapboxLayer
              id="champs-polygons"
              :options="champsLayerOptions"
            />

            <MapboxLayer
              id="champs-outlines"
              :options="champsOutlineLayerOptions"
            />

            <MapboxLayer
              id="champs-labels"
              :options="champsLabelLayerOptions"
            />

            <MapboxLayer
              id="champs-group-labels"
              :options="champsGroupLabelLayerOptions"
            />

            <!-- Géonote pins (rendered first = below weather station markers) -->
            <MapboxMarker
              v-for="gn in géonotesData"
              :key="'gn-' + gn.id"
              :lng-lat="gn.coords"
              anchor="bottom"
            >
              <template #default>
                <div class="geonote-pin" :class="{ 'geonote-pin--hidden': !showGéonotes }">
                  <v-icon class="geonote-pin__icon" size="20">M14,10H19.5L14,4.5V10M5,3H15L21,9V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3M5,12V14H19V12H5M5,16V18H14V16H5Z</v-icon>
                </div>
              </template>
              <template #popup>
                <div class="geonote-popup">
                  <div class="geonote-popup__header">
                    <v-icon size="14" color="warning" class="mr-1">M14,10H19.5L14,4.5V10M5,3H15L21,9V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3M5,12V14H19V12H5M5,16V18H14V16H5Z</v-icon>
                    <span class="geonote-popup__title">Géonote</span>
                    <span class="geonote-popup__date">{{ gn.date }}</span>
                  </div>
                  <div v-if="gn.ferme || gn.champ" class="geonote-popup__meta">
                    <span v-if="gn.ferme">{{ gn.ferme }}</span>
                    <span v-if="gn.ferme && gn.champ"> — </span>
                    <span v-if="gn.champ">{{ gn.champ }}</span>
                  </div>
                  <div class="geonote-popup__note">{{ gn.note }}</div>
                  <img v-if="gn.photoPreview" :src="gn.photoPreview" class="geonote-popup__photo" />
                </div>
              </template>
            </MapboxMarker>

            <!-- Spore capteur markers — rendered in 4 passes (none→zero→low→positive) so red is always on top in DOM -->
            <template v-for="capteur in sporeCapteursNone" :key="'spore-' + capteur.id">
              <MapboxMarker :lng-lat="[capteur.dernièreLongitude, capteur.dernièreLatitude]" anchor="bottom">
                <template #default>
                  <div
                    class="spore-capteur-wrap"
                    :class="{
                      'spore-capteur-wrap--hidden': !showSpores || !canSeeLayer('spores'),
                      'spore-capteur-wrap--expanded': expandedMarkerId === 'spore-' + capteur.id,
                    }"
                    :style="{ '--spore-color': sporeCapteurStyle(capteur).color, '--spore-stroke': sporeCapteurStyle(capteur).stroke }"
                    @click.stop="toggleMarker('spore-' + capteur.id)"
                  >
                    <div class="spore-capteur-box">
                      <v-icon class="spore-capteur-box__icon" size="18">{{ sporeCapteurStyle(capteur).icon }}</v-icon>
                      <div class="spore-capteur-details" @touchmove.stop @wheel.stop>
                        <template v-if="capteur.dernierClientNom || capteur.dernierChampNom">
                          <div class="spore-capteur-details__name">
                            <span v-if="capteur.dernierClientNom">{{ capteur.dernierClientNom }}</span>
                            <span v-if="capteur.dernierClientNom && capteur.dernierChampNom" class="spore-capteur-details__sep"> — </span>
                            <span v-if="capteur.dernierChampNom">{{ capteur.dernierChampNom }}</span>
                          </div>
                          <div class="spore-capteur-details__id">{{ capteur.nom }}</div>
                        </template>
                        <div v-else class="spore-capteur-details__name">{{ capteur.nom }}</div>
                        <template v-if="sporeCapteurMesures.get(capteur.id)?.length">
                          <div class="marker-popup__divider" />
                          <div v-for="groupe in sporeCapteurMesures.get(capteur.id)" :key="groupe.date" class="spore-capteur-mesures__group">
                            <div class="spore-capteur-mesures__date">{{ formatSporeDate(groupe.date) }}</div>
                            <div v-for="item in groupe.items" :key="item.culture + '|' + item.nom" class="spore-capteur-mesures__item">
                              <span class="spore-capteur-mesures__name">
                                {{ item.nom }}
                                <span v-if="!selectedSporeCulture" class="spore-capteur-mesures__culture">{{ item.culture }}</span>
                              </span>
                              <span class="spore-capteur-mesures__val">{{ item.valeur }}</span>
                            </div>
                          </div>
                        </template>
                        <v-btn density="compact" variant="tonal" color="primary" size="x-small" prepend-icon="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z" class="mt-2" block
                          @click.stop="router.push({ path: '/prisme/captures-spores', query: { capteur: capteur.nom } })"
                        >Captures de spores</v-btn>
                      </div>
                    </div>
                    <div class="spore-capteur-pin" />
                  </div>
                </template>
              </MapboxMarker>
            </template>
            <template v-for="capteur in sporeCapteursZero" :key="'spore-' + capteur.id">
              <MapboxMarker :lng-lat="[capteur.dernièreLongitude, capteur.dernièreLatitude]" anchor="bottom">
                <template #default>
                  <div
                    class="spore-capteur-wrap"
                    :class="{
                      'spore-capteur-wrap--hidden': !showSpores || !canSeeLayer('spores'),
                      'spore-capteur-wrap--expanded': expandedMarkerId === 'spore-' + capteur.id,
                    }"
                    :style="{ '--spore-color': sporeCapteurStyle(capteur).color, '--spore-stroke': sporeCapteurStyle(capteur).stroke }"
                    @click.stop="toggleMarker('spore-' + capteur.id)"
                  >
                    <div class="spore-capteur-box">
                      <v-icon class="spore-capteur-box__icon" size="18">{{ sporeCapteurStyle(capteur).icon }}</v-icon>
                      <div class="spore-capteur-details" @touchmove.stop @wheel.stop>
                        <template v-if="capteur.dernierClientNom || capteur.dernierChampNom">
                          <div class="spore-capteur-details__name">
                            <span v-if="capteur.dernierClientNom">{{ capteur.dernierClientNom }}</span>
                            <span v-if="capteur.dernierClientNom && capteur.dernierChampNom" class="spore-capteur-details__sep"> — </span>
                            <span v-if="capteur.dernierChampNom">{{ capteur.dernierChampNom }}</span>
                          </div>
                          <div class="spore-capteur-details__id">{{ capteur.nom }}</div>
                        </template>
                        <div v-else class="spore-capteur-details__name">{{ capteur.nom }}</div>
                        <template v-if="sporeCapteurMesures.get(capteur.id)?.length">
                          <div class="marker-popup__divider" />
                          <div v-for="groupe in sporeCapteurMesures.get(capteur.id)" :key="groupe.date" class="spore-capteur-mesures__group">
                            <div class="spore-capteur-mesures__date">{{ formatSporeDate(groupe.date) }}</div>
                            <div v-for="item in groupe.items" :key="item.culture + '|' + item.nom" class="spore-capteur-mesures__item">
                              <span class="spore-capteur-mesures__name">
                                {{ item.nom }}
                                <span v-if="!selectedSporeCulture" class="spore-capteur-mesures__culture">{{ item.culture }}</span>
                              </span>
                              <span class="spore-capteur-mesures__val">{{ item.valeur }}</span>
                            </div>
                          </div>
                        </template>
                        <v-btn density="compact" variant="tonal" color="primary" size="x-small" prepend-icon="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z" class="mt-2" block
                          @click.stop="router.push({ path: '/prisme/captures-spores', query: { capteur: capteur.nom } })"
                        >Captures de spores</v-btn>
                      </div>
                    </div>
                    <div class="spore-capteur-pin" />
                  </div>
                </template>
              </MapboxMarker>
            </template>
            <template v-for="capteur in sporeCapteursLow" :key="'spore-' + capteur.id">
              <MapboxMarker :lng-lat="[capteur.dernièreLongitude, capteur.dernièreLatitude]" anchor="bottom">
                <template #default>
                  <div
                    class="spore-capteur-wrap"
                    :class="{
                      'spore-capteur-wrap--hidden': !showSpores || !canSeeLayer('spores'),
                      'spore-capteur-wrap--expanded': expandedMarkerId === 'spore-' + capteur.id,
                    }"
                    :style="{ '--spore-color': sporeCapteurStyle(capteur).color, '--spore-stroke': sporeCapteurStyle(capteur).stroke }"
                    @click.stop="toggleMarker('spore-' + capteur.id)"
                  >
                    <div class="spore-capteur-box">
                      <v-icon class="spore-capteur-box__icon" size="18">{{ sporeCapteurStyle(capteur).icon }}</v-icon>
                      <div class="spore-capteur-details" @touchmove.stop @wheel.stop>
                        <template v-if="capteur.dernierClientNom || capteur.dernierChampNom">
                          <div class="spore-capteur-details__name">
                            <span v-if="capteur.dernierClientNom">{{ capteur.dernierClientNom }}</span>
                            <span v-if="capteur.dernierClientNom && capteur.dernierChampNom" class="spore-capteur-details__sep"> — </span>
                            <span v-if="capteur.dernierChampNom">{{ capteur.dernierChampNom }}</span>
                          </div>
                          <div class="spore-capteur-details__id">{{ capteur.nom }}</div>
                        </template>
                        <div v-else class="spore-capteur-details__name">{{ capteur.nom }}</div>
                        <template v-if="sporeCapteurMesures.get(capteur.id)?.length">
                          <div class="marker-popup__divider" />
                          <div v-for="groupe in sporeCapteurMesures.get(capteur.id)" :key="groupe.date" class="spore-capteur-mesures__group">
                            <div class="spore-capteur-mesures__date">{{ formatSporeDate(groupe.date) }}</div>
                            <div v-for="item in groupe.items" :key="item.culture + '|' + item.nom" class="spore-capteur-mesures__item">
                              <span class="spore-capteur-mesures__name">
                                {{ item.nom }}
                                <span v-if="!selectedSporeCulture" class="spore-capteur-mesures__culture">{{ item.culture }}</span>
                              </span>
                              <span class="spore-capteur-mesures__val">{{ item.valeur }}</span>
                            </div>
                          </div>
                        </template>
                        <v-btn density="compact" variant="tonal" color="primary" size="x-small" prepend-icon="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z" class="mt-2" block
                          @click.stop="router.push({ path: '/prisme/captures-spores', query: { capteur: capteur.nom } })"
                        >Captures de spores</v-btn>
                      </div>
                    </div>
                    <div class="spore-capteur-pin" />
                  </div>
                </template>
              </MapboxMarker>
            </template>
            <template v-for="capteur in sporeCapteursPositive" :key="'spore-' + capteur.id">
              <MapboxMarker :lng-lat="[capteur.dernièreLongitude, capteur.dernièreLatitude]" anchor="bottom">
                <template #default>
                  <div
                    class="spore-capteur-wrap"
                    :class="{
                      'spore-capteur-wrap--hidden': !showSpores || !canSeeLayer('spores'),
                      'spore-capteur-wrap--expanded': expandedMarkerId === 'spore-' + capteur.id,
                    }"
                    :style="{ '--spore-color': sporeCapteurStyle(capteur).color, '--spore-stroke': sporeCapteurStyle(capteur).stroke }"
                    @click.stop="toggleMarker('spore-' + capteur.id)"
                  >
                    <div class="spore-capteur-box">
                      <v-icon class="spore-capteur-box__icon" size="18">{{ sporeCapteurStyle(capteur).icon }}</v-icon>
                      <div class="spore-capteur-details" @touchmove.stop @wheel.stop>
                        <template v-if="capteur.dernierClientNom || capteur.dernierChampNom">
                          <div class="spore-capteur-details__name">
                            <span v-if="capteur.dernierClientNom">{{ capteur.dernierClientNom }}</span>
                            <span v-if="capteur.dernierClientNom && capteur.dernierChampNom" class="spore-capteur-details__sep"> — </span>
                            <span v-if="capteur.dernierChampNom">{{ capteur.dernierChampNom }}</span>
                          </div>
                          <div class="spore-capteur-details__id">{{ capteur.nom }}</div>
                        </template>
                        <div v-else class="spore-capteur-details__name">{{ capteur.nom }}</div>
                        <template v-if="sporeCapteurMesures.get(capteur.id)?.length">
                          <div class="marker-popup__divider" />
                          <div v-for="groupe in sporeCapteurMesures.get(capteur.id)" :key="groupe.date" class="spore-capteur-mesures__group">
                            <div class="spore-capteur-mesures__date">{{ formatSporeDate(groupe.date) }}</div>
                            <div v-for="item in groupe.items" :key="item.culture + '|' + item.nom" class="spore-capteur-mesures__item">
                              <span class="spore-capteur-mesures__name">
                                {{ item.nom }}
                                <span v-if="!selectedSporeCulture" class="spore-capteur-mesures__culture">{{ item.culture }}</span>
                              </span>
                              <span class="spore-capteur-mesures__val">{{ item.valeur }}</span>
                            </div>
                          </div>
                        </template>
                        <v-btn density="compact" variant="tonal" color="primary" size="x-small" prepend-icon="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z" class="mt-2" block
                          @click.stop="router.push({ path: '/prisme/captures-spores', query: { capteur: capteur.nom } })"
                        >Captures de spores</v-btn>
                      </div>
                    </div>
                    <div class="spore-capteur-pin" />
                  </div>
                </template>
              </MapboxMarker>
            </template>

            <!-- Weather station markers (on top of spore markers) -->
            <MapboxMarker
              v-for="value in sourcesData.features" :key="value.id"
              :lng-lat="[value.geometry.coordinates[0], value.geometry.coordinates[1]]"
              anchor="bottom">
              <template #default>
                <div
                  class="custom-marker-wrap"
                  :class="{ 'custom-marker-wrap--expanded': expandedMarkerId === value.id, 'custom-marker-wrap--hidden': !showStations }"
                  :style="{ '--accent': getMarkerAccentColor(value.properties) }"
                  @click.stop="toggleMarker(value.id)"
                >
                  <div class="custom-marker">
                    <!-- main value -->
                    <div class="custom-marker__value">
                      <template v-if="selectedMetric === 'mildiou'">
                        <span v-if="value.properties.mildiou < 0" class="custom-marker__dash">—</span>
                        <span v-else-if="value.properties.mildiou >= 1" class="custom-marker__risk">⚠</span>
                        <span v-else class="custom-marker__ok">✓</span>
                      </template>
                      <template v-else>{{ getMarkerText(value.properties) }}</template>
                    </div>

                    <!-- sparkline -->
                    <svg
                      v-if="getSparklinePoints(value.id)"
                      class="custom-marker__spark"
                      width="56" height="20"
                      viewBox="0 0 56 20"
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="none"
                    >
                      <polygon
                        :points="getSparklineFill(value.id)"
                        :style="{ fill: primaryColor, fillOpacity: '0.18' }"
                      />
                      <polyline
                        :points="getSparklinePoints(value.id)"
                        fill="none"
                        :style="{ stroke: primaryColor, strokeWidth: '1.5', strokeLinejoin: 'round', strokeLinecap: 'round' }"
                      />
                    </svg>
                    <div v-else class="custom-marker__spark-placeholder" />

                    <!-- forecast dot -->
                    <div v-if="value.properties.prevision" class="custom-marker__forecast-dot" />

                    <!-- ── Inline expandable details ───────────────── -->
                    <div class="custom-marker__details">
                      <!-- Header -->
                      <div class="custom-marker__details-header">
                        <span class="custom-marker__details-title">{{ value.properties.name }}</span>
                        <v-btn
                          icon
                          size="x-small"
                          variant="text"
                          density="compact"
                          class="marker-popup__open-btn"
                          @click.stop="openSourceDialog(value)"
                        >
                          <v-icon size="16">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon>
                        </v-btn>
                      </div>

                      <!-- Forecast badge -->
                      <div v-if="value.properties.prevision" class="marker-popup__forecast-badge">
                        <v-icon size="12">M15,4A8,8 0 0,1 23,12A8,8 0 0,1 15,20A8,8 0 0,1 7,12A8,8 0 0,1 15,4M15,6A6,6 0 0,0 9,12A6,6 0 0,0 15,18A6,6 0 0,0 21,12A6,6 0 0,0 15,6M14,8H15.5V11.78L17.83,14.11L16.77,15.17L14,12.4V8M2,18A1,1 0 0,1 1,17A1,1 0 0,1 2,16H5.83C6.14,16.71 6.54,17.38 7,18H2M3,13A1,1 0 0,1 2,12A1,1 0 0,1 3,11H5.05L5,12L5.05,13H3M4,8A1,1 0 0,1 3,7A1,1 0 0,1 4,6H7C6.54,6.62 6.14,7.29 5.83,8H4Z</v-icon> Prévision
                      </div>

                      <div class="marker-popup__divider" />

                      <!-- Metrics -->
                      <div class="marker-popup__metrics">
                        <div class="marker-popup__row" :class="{ active: selectedMetric === 'temperature' }">
                          <v-icon size="16" class="marker-popup__icon">M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z</v-icon>
                          <span class="marker-popup__label">Température</span>
                          <span class="marker-popup__value">
                            {{ value.properties.temperature }} °C
                            <span v-if="value.properties.isRange" class="marker-popup__subvalue">
                              ({{ value.properties.temperatureMin }}–{{ value.properties.temperatureMax }})
                            </span>
                          </span>
                        </div>

                        <div class="marker-popup__row" :class="{ active: selectedMetric === 'humidity' }">
                          <v-icon size="16" class="marker-popup__icon">M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z</v-icon>
                          <span class="marker-popup__label">Humidité</span>
                          <span class="marker-popup__value">
                            {{ value.properties.humidity }} %
                            <span v-if="value.properties.isRange" class="marker-popup__subvalue">
                              ({{ value.properties.humidityMin }}–{{ value.properties.humidityMax }})
                            </span>
                          </span>
                        </div>

                        <div class="marker-popup__row" :class="{ active: selectedMetric === 'precip' }">
                          <v-icon size="16" class="marker-popup__icon">M6,14.03A1,1 0 0,1 7,15.03C7,15.58 6.55,16.03 6,16.03C3.24,16.03 1,13.79 1,11.03C1,8.27 3.24,6.03 6,6.03C7,3.68 9.3,2.03 12,2.03C15.43,2.03 18.24,4.69 18.5,8.06L19,8.03A4,4 0 0,1 23,12.03C23,14.23 21.21,16.03 19,16.03H18C17.45,16.03 17,15.58 17,15.03C17,14.47 17.45,14.03 18,14.03H19A2,2 0 0,0 21,12.03A2,2 0 0,0 19,10.03H17V9.03C17,6.27 14.76,4.03 12,4.03C9.5,4.03 7.45,5.84 7.06,8.21C6.73,8.09 6.37,8.03 6,8.03A3,3 0 0,0 3,11.03A3,3 0 0,0 6,14.03M12,14.15C12.18,14.39 12.37,14.66 12.56,14.94C13,15.56 14,17.03 14,18C14,19.11 13.1,20 12,20A2,2 0 0,1 10,18C10,17.03 11,15.56 11.44,14.94C11.63,14.66 11.82,14.4 12,14.15M12,11.03L11.5,11.59C11.5,11.59 10.65,12.55 9.79,13.81C8.93,15.06 8,16.56 8,18A4,4 0 0,0 12,22A4,4 0 0,0 16,18C16,16.56 15.07,15.06 14.21,13.81C13.35,12.55 12.5,11.59 12.5,11.59</v-icon>
                          <span class="marker-popup__label">Précipitations</span>
                          <span class="marker-popup__value">
                            {{ value.properties.precip }} mm
                            <span v-if="value.properties.isRange" class="marker-popup__subvalue">(total)</span>
                          </span>
                        </div>

                        <div class="marker-popup__row" :class="{ active: selectedMetric === 'lacy' }">
                          <v-icon size="16" class="marker-popup__icon">M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z</v-icon>
                          <span class="marker-popup__label">Brûlure (Botrytis)</span>
                          <span class="marker-popup__value">
                            {{ value.properties.lacy }} %
                            <span v-if="value.properties.isRange" class="marker-popup__subvalue">(max)</span>
                          </span>
                        </div>

                        <div class="marker-popup__row" :class="{ active: selectedMetric === 'mildiou' }">
                          <v-icon size="16" class="marker-popup__icon">M23,12L20.56,14.78L20.9,18.46L17.29,19.28L15.4,22.46L12,21L8.6,22.47L6.71,19.29L3.1,18.47L3.44,14.78L1,12L3.44,9.21L3.1,5.53L6.71,4.72L8.6,1.54L12,3L15.4,1.54L17.29,4.72L20.9,5.54L20.56,9.22L23,12M20.33,12L18.5,9.89L18.74,7.1L16,6.5L14.58,4.07L12,5.18L9.42,4.07L8,6.5L5.26,7.09L5.5,9.88L3.67,12L5.5,14.1L5.26,16.9L8,17.5L9.42,19.93L12,18.81L14.58,19.92L16,17.5L18.74,16.89L18.5,14.1L20.33,12M11,15H13V17H11V15M11,7H13V13H11V7</v-icon>
                          <span class="marker-popup__label">Mildiou</span>
                          <span class="marker-popup__value">
                            <template v-if="value.properties.mildiou < 0">—</template>
                            <template v-else-if="value.properties.mildiou >= 1">
                              ⚠️ <span style="color: rgb(220,38,38); font-weight:700;">Risque détecté</span>
                            </template>
                            <template v-else>
                              ✅ <span style="color: rgb(34,197,94); font-weight:700;">OK</span>
                            </template>
                            <span v-if="value.properties.isRange" class="marker-popup__subvalue">(période)</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- ──────────────────────────────────────────────── -->
                  </div>

                  <!-- pin tip (sibling of box so wrapper bottom = tip) -->
                  <div class="custom-marker__pin" />
                </div>
              </template>
            </MapboxMarker>

            <!--<MapboxCluster :data="weatherStations" :cluster-min-points="50" />-->
            <!--<MapboxCluster :data="sourcesData" :cluster-min-points="50" />-->
            <!--<MapboxMarker :lng-lat="mapCenter" popup>
              <template #popup>
                <ChartLine />
              </template>
            </MapboxMarker>-->

            <!-- GPS user location marker -->
            <MapboxMarker v-if="userLocation" :lng-lat="userLocation" anchor="center">
              <template #default>
                <div class="user-location-marker">
                  <div class="user-location-marker__dot" />
                  <div class="user-location-marker__pulse" />
                </div>
              </template>
            </MapboxMarker>

    </MapboxMap>

    <!-- ── Map right-click context menu ───────────────────────────────────── -->
    <div
      :style="{
        position: 'absolute',
        left: mapContextMenu.x + 'px',
        top:  mapContextMenu.y + 'px',
        width: '1px',
        height: '1px',
        zIndex: 20,
        pointerEvents: mapContextMenu.show ? 'auto' : 'none',
      }"
    >
      <v-menu
        v-model="mapContextMenu.show"
        location="bottom start"
        :offset="[4, 4]"
        activator="parent"
        :close-on-content-click="true"
      >
        <v-list density="compact" nav min-width="210"
          :style="{ background: panelBg, backdropFilter: 'blur(12px)', boxShadow: panelShadow }"
        >
          <v-list-item
            prepend-icon="M5 19V5H12V12H19V13C19.7 13 20.37 13.13 21 13.35V9L15 3H5C3.89 3 3 3.89 3 5V19C3 20.1 3.89 21 5 21H13.35C13.13 20.37 13 19.7 13 19H5M14 4.5L19.5 10H14V4.5M23 18V20H20V23H18V20H15V18H18V15H20V18H23Z"
            :style="{ color: panelTextSub, opacity: 0.45, pointerEvents: 'none', cursor: 'default' }"
            disabled
          >
            <v-list-item-title>Ajouter une géonote ici</v-list-item-title>
            <v-list-item-subtitle :style="{ color: panelTextSub, fontSize: '10px' }">
              {{ mapContextMenu.lat.toFixed(5) }}, {{ mapContextMenu.lng.toFixed(5) }}
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Mobile: FAB to open layer drawer -->
    <v-btn
      v-if="display.smAndDown.value"
      class="layer-fab"
      icon
      size="small"
      elevation="6"
      color="primary"
      @click="layerDrawerOpen = true"
    >
      <v-icon>M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z</v-icon>
    </v-btn>

    <!-- Mobile: bottom-sheet layer panel -->
    <v-bottom-sheet v-if="display.smAndDown.value" v-model="layerDrawerOpen" :style="{ zIndex: 20 }">
      <v-card rounded="t-xl" :style="{ background: panelBg, backdropFilter: 'blur(12px)' }">
        <div style="padding: 8px 16px 4px; display:flex; align-items:center; justify-content:space-between;">
          <div style="display:flex; align-items:center; gap:6px;">
            <v-icon size="14" :style="{ color: panelTextSub }">M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z</v-icon>
            <span :style="{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: panelTextSub }">Couches</span>
          </div>
          <v-btn icon size="x-small" variant="text" @click="layerDrawerOpen = false"><v-icon>M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z</v-icon></v-btn>
        </div>
        <v-divider />
        <div style="overflow-y: auto; max-height: 55vh;">
          <v-list density="compact" nav open-strategy="multiple" :opened="layerPanelGroups" class="layer-tree" @update:opened="layerPanelGroups = $event">
            <v-list-group value="stations">
              <template #activator="{ props }"><v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header"><template #prepend><v-switch v-model="showStations" density="compact" hide-details color="primary" class="layer-tree__switch" @click.stop /></template><v-list-item-title class="layer-tree__group-title">Stations météo</v-list-item-title></v-list-item></template>
              <v-list-item v-for="item in layerItems" :key="item.value" density="compact" rounded="lg" :active="showStations && selectedMetric === item.value" :disabled="!showStations" color="primary" class="layer-tree__leaf" @click="selectedMetric = item.value"><template #prepend><v-icon :icon="item.icon" size="13" color="primary" class="mr-1" /></template><v-list-item-title class="layer-tree__label">{{ item.label }}</v-list-item-title></v-list-item>
              <v-list-item density="compact" rounded="lg" :active="showStations && selectedMetric === 'lacy'" :disabled="!showStations" color="primary" class="layer-tree__leaf" @click="selectedMetric = 'lacy'"><template #prepend><v-icon icon="M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z" size="13" color="primary" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Brûlure (Botrytis)</v-list-item-title></v-list-item>
              <v-list-item density="compact" rounded="lg" :active="showStations && selectedMetric === 'mildiou'" :disabled="!showStations" color="primary" class="layer-tree__leaf" @click="selectedMetric = 'mildiou'"><template #prepend><v-icon icon="M23,12L20.56,14.78L20.9,18.46L17.29,19.28L15.4,22.46L12,21L8.6,22.47L6.71,19.29L3.1,18.47L3.44,14.78L1,12L3.44,9.21L3.1,5.53L6.71,4.72L8.6,1.54L12,3L15.4,1.54L17.29,4.72L20.9,5.54L20.56,9.22L23,12M20.33,12L18.5,9.89L18.74,7.1L16,6.5L14.58,4.07L12,5.18L9.42,4.07L8,6.5L5.26,7.09L5.5,9.88L3.67,12L5.5,14.1L5.26,16.9L8,17.5L9.42,19.93L12,18.81L14.58,19.92L16,17.5L18.74,16.89L18.5,14.1L20.33,12M11,15H13V17H11V15M11,7H13V13H11V7" size="13" color="primary" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Mildiou</v-list-item-title></v-list-item>
              <v-list-item density="compact" rounded="lg" class="layer-tree__leaf"><template #prepend><v-icon icon="M19.07,4.93L17.66,6.34C19.1,7.79 20,9.79 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12C4,7.92 7.05,4.56 11,4.07V6.09C8.16,6.57 6,9.03 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12C18,10.34 17.33,8.84 16.24,7.76L14.83,9.17C15.55,9.9 16,10.9 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12C8,10.14 9.28,8.59 11,8.14V10.28C10.4,10.63 10,11.26 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12C14,11.26 13.6,10.62 13,10.28V2H12A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,9.24 20.88,6.74 19.07,4.93Z" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Radar</v-list-item-title><template #append><v-switch v-model="showRadar" density="compact" hide-details color="primary" class="layer-tree__switch" /></template></v-list-item>
            </v-list-group>
            <v-list-group v-if="canSeeLayer('spores')" value="spores">
              <template #activator="{ props }"><v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header"><template #prepend><v-switch v-model="showSpores" density="compact" hide-details color="primary" class="layer-tree__switch" @click.stop /></template><v-list-item-title class="layer-tree__group-title">Capteurs de spores</v-list-item-title><template #append><v-badge v-if="sporeCapteursData.length" :content="sporeCapteursData.length" color="primary" inline class="mr-1" /></template></v-list-item></template>
              <template v-if="sporeCulturesDisponibles.length">
                <div class="layer-tree__subheader" :style="{ color: panelTextSub }">Culture</div>
                <v-list-item density="compact" rounded="lg" :active="!selectedSporeCulture" color="primary" class="layer-tree__leaf" @click="selectedSporeCulture = ''"><template #prepend><v-icon size="16" class="mr-1" :color="!selectedSporeCulture ? 'primary' : undefined">{{ !selectedSporeCulture ? 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7Z' : 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' }}</v-icon></template><v-list-item-title class="layer-tree__label" style="overflow:hidden; text-overflow:ellipsis;">Toutes</v-list-item-title></v-list-item>
                <v-list-item v-for="culture in sporeCulturesDisponibles" :key="'culture-' + culture" density="compact" rounded="lg" :active="selectedSporeCulture === culture" color="primary" class="layer-tree__leaf" @click="selectedSporeCulture = culture"><template #prepend><v-icon size="16" class="mr-1" :color="selectedSporeCulture === culture ? 'primary' : undefined">{{ selectedSporeCulture === culture ? 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7Z' : 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' }}</v-icon></template><v-list-item-title class="layer-tree__label" style="overflow:hidden; text-overflow:ellipsis;">{{ culture }}</v-list-item-title></v-list-item>
              </template>
              <div class="layer-tree__subheader" :style="{ color: panelTextSub }">Fermes</div>
              <div class="layer-tree__select-wrap">
                <v-select v-model="selectedSporeClients" :items="sporeClientItems" multiple density="compact" variant="outlined" hide-details placeholder="Aucun" :menu-props="{ maxHeight: 280 }" class="layer-tree__select">
                  <template #selection="{ index }"><span v-if="index === 0" class="layer-tree__select-summary" :style="{ color: panelTextMain }">{{ sporeClientsSummary }}</span></template>
                  <template #prepend-item><div class="layer-tree__all-none"><v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedSporeClients = [...sporeClientColorMap.keys()]">Tous</v-btn><v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedSporeClients = []">Aucun</v-btn></div><v-divider /></template>
                  <template #item="{ props: itemProps, item }"><v-list-item v-bind="itemProps" density="compact" class="layer-tree__producer"><template #prepend="{ isSelected }"><v-checkbox-btn :model-value="isSelected" density="compact" :color="item.raw.color" class="me-1" /></template><template #append><span :style="{ width: '8px', height: '8px', borderRadius: '50%', background: item.raw.color, flexShrink: 0, display: 'inline-block' }" /></template></v-list-item></template>
                </v-select>
              </div>
            </v-list-group>
            <v-list-group v-if="hasChamps" value="champs">
              <template #activator="{ props }"><v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header"><template #prepend><v-switch v-model="showChamps" density="compact" hide-details color="primary" class="layer-tree__switch" @click.stop /></template><v-list-item-title class="layer-tree__group-title">Champs</v-list-item-title></v-list-item></template>
              <div class="layer-tree__all-none"><v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedProducers = [...producerColorMap.keys()]">Tous</v-btn><v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedProducers = []">Aucun</v-btn></div>
              <v-list-item v-for="[producer, color] in producerColorMap" :key="producer" density="compact" rounded="lg" class="layer-tree__leaf layer-tree__producer"><template #prepend><v-checkbox v-model="selectedProducers" :value="producer" density="compact" hide-details :color="color" class="layer-tree__checkbox" /></template><v-list-item-title class="layer-tree__label" style="overflow:hidden; text-overflow:ellipsis;">{{ producer }}</v-list-item-title><template #append><span :style="{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }" /></template></v-list-item>
            </v-list-group>
            <v-list-group value="geonotes">
              <template #activator="{ props }"><v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header"><template #prepend><v-switch v-model="showGéonotes" density="compact" hide-details color="warning" class="layer-tree__switch" :disabled="géonotesData.length === 0" @click.stop /></template><v-list-item-title class="layer-tree__group-title">Géonotes</v-list-item-title><template #append><v-badge v-if="géonotesData.length" :content="géonotesData.length" color="warning" inline class="mr-1" /><v-icon size="16" :icon="layerPanelGroups.includes('geonotes') ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'" style="opacity:0.6" /></template></v-list-item></template>
              <v-list-item density="compact" rounded="lg" disabled class="layer-tree__leaf"><template #prepend><v-icon icon="M19 7H16.19C15.74 6.2 15.12 5.5 14.37 5L16 3.41L14.59 2L12.42 4.17C11.96 4.06 11.5 4 11 4S10.05 4.06 9.59 4.17L7.41 2L6 3.41L7.62 5C6.87 5.5 6.26 6.21 5.81 7H3V9H5.09C5.03 9.33 5 9.66 5 10V11H3V13H5V14C5 14.34 5.03 14.67 5.09 15H3V17H5.81C7.26 19.5 10.28 20.61 13 19.65V19C13 18.43 13.09 17.86 13.25 17.31C12.59 17.76 11.8 18 11 18C8.79 18 7 16.21 7 14V10C7 7.79 8.79 6 11 6S15 7.79 15 10V14C15 14.19 15 14.39 14.95 14.58C15.54 14.04 16.24 13.62 17 13.35V13H19V11H17V10C17 9.66 16.97 9.33 16.91 9H19V7M21.34 16L17.75 19.59L16.16 18L15 19.16L17.75 22.16L22.5 17.41L21.34 16M13 9V11H9V9H13M13 13V15H9V13H13Z" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Dépistage</v-list-item-title><template #append><v-chip size="x-small" label>Bientôt</v-chip></template></v-list-item>
              <v-list-item density="compact" rounded="lg" disabled class="layer-tree__leaf"><template #prepend><v-icon icon="M15 3H5A2 2 0 0 0 3 5V19A2 2 0 0 0 5 21H19A2 2 0 0 0 21 19V9L15 3M19 19H5V5H14V10H19M17 14H7V12H17M14 17H7V15H14" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Notes personnelles</v-list-item-title><template #append><v-chip size="x-small" label>Bientôt</v-chip></template></v-list-item>
            </v-list-group>
            <v-list-group value="capteurs">
              <template #activator="{ props }"><v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header"><template #prepend><v-switch v-model="showCapteurs" density="compact" hide-details color="primary" class="layer-tree__switch" disabled @click.stop /></template><v-list-item-title class="layer-tree__group-title">Capteurs</v-list-item-title></v-list-item></template>
              <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon"><template #prepend><v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Compteur d'eau</v-list-item-title><template #append><v-btn size="x-small" variant="text" icon tag="a" href="https://www.agrimetrie.com/compteur-deau" target="_blank" @click.stop><v-icon size="13">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon></v-btn></template></v-list-item>
              <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon"><template #prepend><v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Thermomètres</v-list-item-title><template #append><v-btn size="x-small" variant="text" icon tag="a" href="https://www.agrimetrie.com/capteur-de-temperature-et-dhumidite" target="_blank" @click.stop><v-icon size="13">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon></v-btn></template></v-list-item>
              <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon"><template #prepend><v-icon icon="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,14.4 19,16.5 17.3,18C15.9,16.7 14,16 12,16C10,16 8.2,16.7 6.7,18C5,16.5 4,14.4 4,12A8,8 0 0,1 12,4M14,5.89C13.62,5.9 13.26,6.15 13.1,6.54L11.81,9.77L11.71,10C11,10.13 10.41,10.6 10.14,11.26C9.73,12.29 10.23,13.45 11.26,13.86C12.29,14.27 13.45,13.77 13.86,12.74C14.12,12.08 14,11.32 13.57,10.76L13.67,10.5L14.96,7.29L14.97,7.26C15.17,6.75 14.92,6.17 14.41,5.96C14.28,5.91 14.15,5.89 14,5.89M10,6A1,1 0 0,0 9,7A1,1 0 0,0 10,8A1,1 0 0,0 11,7A1,1 0 0,0 10,6M7,9A1,1 0 0,0 6,10A1,1 0 0,0 7,11A1,1 0 0,0 8,10A1,1 0 0,0 7,9M17,9A1,1 0 0,0 16,10A1,1 0 0,0 17,11A1,1 0 0,0 18,10A1,1 0 0,0 17,9Z" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Tensiomètres</v-list-item-title><template #append><v-btn size="x-small" variant="text" icon tag="a" href="https://www.agrimetrie.com/tensiometre" target="_blank" @click.stop><v-icon size="13">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon></v-btn></template></v-list-item>
              <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon"><template #prepend><v-icon icon="M18,15A3,3 0 0,1 21,18A3,3 0 0,1 18,21C16.69,21 15.58,20.17 15.17,19H14V17H15.17C15.58,15.83 16.69,15 18,15M18,17A1,1 0 0,0 17,18A1,1 0 0,0 18,19A1,1 0 0,0 19,18A1,1 0 0,0 18,17M18,8A1.43,1.43 0 0,0 19.43,6.57C19.43,5.78 18.79,5.14 18,5.14C17.21,5.14 16.57,5.78 16.57,6.57A1.43,1.43 0 0,0 18,8M18,2.57A4,4 0 0,1 22,6.57C22,9.56 18,14 18,14C18,14 14,9.56 14,6.57A4,4 0 0,1 18,2.57M8.83,17H10V19H8.83C8.42,20.17 7.31,21 6,21A3,3 0 0,1 3,18C3,16.69 3.83,15.58 5,15.17V14H7V15.17C7.85,15.47 8.53,16.15 8.83,17M6,17A1,1 0 0,0 5,18A1,1 0 0,0 6,19A1,1 0 0,0 7,18A1,1 0 0,0 6,17M6,3A3,3 0 0,1 9,6C9,7.31 8.17,8.42 7,8.83V10H5V8.83C3.83,8.42 3,7.31 3,6A3,3 0 0,1 6,3M6,5A1,1 0 0,0 5,6A1,1 0 0,0 6,7A1,1 0 0,0 7,6A1,1 0 0,0 6,5M11,19V17H13V19H11M7,13H5V11H7V13Z" size="13" class="mr-1" /></template><v-list-item-title class="layer-tree__label">Suivi GPS</v-list-item-title></v-list-item>
            </v-list-group>
            <v-list-item density="compact" rounded="lg" :active="!!userLocation" color="primary" class="layer-tree__group-header" @click="userLocation ? (userLocation = null) : locateUser()"><template #prepend><v-icon icon="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z" size="14" class="mr-1" :color="userLocation ? 'primary' : undefined" /></template><v-list-item-title class="layer-tree__group-title">Ma position</v-list-item-title><template #append><v-progress-circular v-if="locating" indeterminate size="16" width="2" color="primary" /><v-icon v-else-if="userLocation" icon="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" size="14" color="primary" /></template></v-list-item>
          </v-list>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Desktop: layer tree panel -->
    <v-card v-if="!display.smAndDown.value" class="controls-overlay-right" elevation="6" :style="{ background: panelBg, boxShadow: panelShadow, backdropFilter: 'blur(10px)' }">
      <!-- Header -->
      <div style="padding: 4px 8px 2px; display:flex; align-items:center;">
        <v-icon size="12" style="margin-right:5px;" :style="{ color: panelTextSub }">M12,18.54L19.37,12.8L21,14.07L12,21.07L3,14.07L4.62,12.81L12,18.54M12,16L3,9L12,2L21,9L12,16M12,4.53L6.26,9L12,13.47L17.74,9L12,4.53Z</v-icon>
        <span :style="{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: panelTextSub }">Couches</span>
      </div>
      <v-divider />

      <!-- Tree -->
      <div style="overflow-y: auto; max-height: 70vh;">
        <v-list
          density="compact"
          nav
          open-strategy="multiple"
          :opened="layerPanelGroups"
          class="layer-tree"
          @update:opened="layerPanelGroups = $event"
        >
          <!-- ── Stations météo ───────────────────────────────────── -->
          <v-list-group value="stations">
            <template #activator="{ props }">
              <v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header">
                <template #prepend>
                  <v-switch
                    v-model="showStations"
                    density="compact"
                    hide-details
                    color="primary"
                    class="layer-tree__switch"
                    @click.stop
                  />
                </template>
                <v-list-item-title class="layer-tree__group-title">Stations météo</v-list-item-title>
              </v-list-item>
            </template>

            <!-- Metric items -->
            <v-list-item
              v-for="item in layerItems"
              :key="item.value"
              density="compact"
              rounded="lg"
              :active="showStations && selectedMetric === item.value"
              :disabled="!showStations"
              color="primary"
              class="layer-tree__leaf"
              @click="selectedMetric = item.value"
            >
              <template #prepend>
                <v-icon :icon="item.icon" size="13" color="primary" class="mr-1" />
              </template>
              <v-list-item-title class="layer-tree__label">{{ item.label }}</v-list-item-title>
            </v-list-item>

            <!-- Brûlure (Botrytis) -->
            <v-list-item
              density="compact"
              rounded="lg"
              :active="showStations && selectedMetric === 'lacy'"
              :disabled="!showStations"
              color="primary"
              class="layer-tree__leaf"
              @click="selectedMetric = 'lacy'"
            >
              <template #prepend>
                <v-icon icon="M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z" size="13" color="primary" class="mr-1" />
              </template>
              <v-list-item-title class="layer-tree__label">Brûlure (Botrytis)</v-list-item-title>
            </v-list-item>

            <!-- Mildiou -->
            <v-list-item
              density="compact"
              rounded="lg"
              :active="showStations && selectedMetric === 'mildiou'"
              :disabled="!showStations"
              color="primary"
              class="layer-tree__leaf"
              @click="selectedMetric = 'mildiou'"
            >
              <template #prepend>
                <v-icon icon="M23,12L20.56,14.78L20.9,18.46L17.29,19.28L15.4,22.46L12,21L8.6,22.47L6.71,19.29L3.1,18.47L3.44,14.78L1,12L3.44,9.21L3.1,5.53L6.71,4.72L8.6,1.54L12,3L15.4,1.54L17.29,4.72L20.9,5.54L20.56,9.22L23,12M20.33,12L18.5,9.89L18.74,7.1L16,6.5L14.58,4.07L12,5.18L9.42,4.07L8,6.5L5.26,7.09L5.5,9.88L3.67,12L5.5,14.1L5.26,16.9L8,17.5L9.42,19.93L12,18.81L14.58,19.92L16,17.5L18.74,16.89L18.5,14.1L20.33,12M11,15H13V17H11V15M11,7H13V13H11V7" size="13" color="primary" class="mr-1" />
              </template>
              <v-list-item-title class="layer-tree__label">Mildiou</v-list-item-title>
            </v-list-item>

            <!-- Radar -->
            <v-list-item density="compact" rounded="lg" class="layer-tree__leaf">
              <template #prepend>
                <v-icon icon="M19.07,4.93L17.66,6.34C19.1,7.79 20,9.79 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12C4,7.92 7.05,4.56 11,4.07V6.09C8.16,6.57 6,9.03 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12C18,10.34 17.33,8.84 16.24,7.76L14.83,9.17C15.55,9.9 16,10.9 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12C8,10.14 9.28,8.59 11,8.14V10.28C10.4,10.63 10,11.26 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12C14,11.26 13.6,10.62 13,10.28V2H12A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,9.24 20.88,6.74 19.07,4.93Z" size="13" class="mr-1" />
              </template>
              <v-list-item-title class="layer-tree__label">Radar</v-list-item-title>
              <template #append>
                <v-switch
                  v-model="showRadar"
                  density="compact"
                  hide-details
                  color="primary"
                  class="layer-tree__switch"
                />
              </template>
            </v-list-item>
          </v-list-group>

          <!-- ── Capteurs de spores ──────────────────────────────── -->
          <v-list-group v-if="canSeeLayer('spores')" value="spores">
            <template #activator="{ props }">
              <v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header">
                <template #prepend>
                  <v-switch
                    v-model="showSpores"
                    density="compact"
                    hide-details
                    color="primary"
                    class="layer-tree__switch"
                    @click.stop
                  />
                </template>
                <v-list-item-title class="layer-tree__group-title">Capteurs de spores</v-list-item-title>
                <template #append>
                  <v-badge v-if="sporeCapteursData.length" :content="sporeCapteursData.length" color="primary" inline class="mr-1" />
                </template>
              </v-list-item>
            </template>

            <!-- Culture filter (single-select: a specific culture, or none = all sensors) -->
            <template v-if="sporeCulturesDisponibles.length">
              <div class="layer-tree__subheader" :style="{ color: panelTextSub }">Culture</div>
              <v-list-item
                density="compact"
                rounded="lg"
                :active="!selectedSporeCulture"
                color="primary"
                class="layer-tree__leaf"
                @click="selectedSporeCulture = ''"
              >
                <template #prepend>
                  <v-icon size="16" class="mr-1" :color="!selectedSporeCulture ? 'primary' : undefined">{{ !selectedSporeCulture ? 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7Z' : 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' }}</v-icon>
                </template>
                <v-list-item-title class="layer-tree__label">Toutes</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-for="culture in sporeCulturesDisponibles"
                :key="'culture-' + culture"
                density="compact"
                rounded="lg"
                :active="selectedSporeCulture === culture"
                color="primary"
                class="layer-tree__leaf"
                @click="selectedSporeCulture = culture"
              >
                <template #prepend>
                  <v-icon size="16" class="mr-1" :color="selectedSporeCulture === culture ? 'primary' : undefined">{{ selectedSporeCulture === culture ? 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7Z' : 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z' }}</v-icon>
                </template>
                <v-list-item-title class="layer-tree__label" style="overflow:hidden; text-overflow:ellipsis;">{{ culture }}</v-list-item-title>
              </v-list-item>
            </template>

            <!-- Ferme filter -->
            <div class="layer-tree__subheader" :style="{ color: panelTextSub }">Fermes</div>
            <div class="layer-tree__select-wrap">
              <v-select
                v-model="selectedSporeClients"
                :items="sporeClientItems"
                multiple
                density="compact"
                variant="outlined"
                hide-details
                placeholder="Aucun"
                :menu-props="{ maxHeight: 320 }"
                class="layer-tree__select"
              >
                <template #selection="{ index }">
                  <span v-if="index === 0" class="layer-tree__select-summary" :style="{ color: panelTextMain }">{{ sporeClientsSummary }}</span>
                </template>
                <template #prepend-item>
                  <div class="layer-tree__all-none">
                    <v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedSporeClients = [...sporeClientColorMap.keys()]">Tous</v-btn>
                    <v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedSporeClients = []">Aucun</v-btn>
                  </div>
                  <v-divider />
                </template>
                <template #item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps" density="compact" class="layer-tree__producer">
                    <template #prepend="{ isSelected }">
                      <v-checkbox-btn :model-value="isSelected" density="compact" :color="item.raw.color" class="me-1" />
                    </template>
                    <template #append>
                      <span :style="{ width: '8px', height: '8px', borderRadius: '50%', background: item.raw.color, flexShrink: 0, display: 'inline-block' }" />
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </div>

          </v-list-group>

          <!-- ── Champs ───────────────────────────────────────────── -->
          <v-list-group v-if="hasChamps" value="champs">
            <template #activator="{ props }">
              <v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header">
                <template #prepend>
                  <v-switch
                    v-model="showChamps"
                    density="compact"
                    hide-details
                    color="primary"
                    class="layer-tree__switch"
                    @click.stop
                  />
                </template>
                <v-list-item-title class="layer-tree__group-title">Champs</v-list-item-title>
              </v-list-item>
            </template>

            <!-- All / None -->
            <div class="layer-tree__all-none">
              <v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedProducers = [...producerColorMap.keys()]">Tous</v-btn>
              <v-btn size="x-small" variant="text" :style="{ color: panelTextSub, fontSize: '10px' }" @click="selectedProducers = []">Aucun</v-btn>
            </div>

            <!-- Per-producer checkboxes -->
            <v-list-item
              v-for="[producer, color] in producerColorMap"
              :key="producer"
              density="compact"
              rounded="lg"
              class="layer-tree__leaf layer-tree__producer"
            >
              <template #prepend>
                <v-checkbox
                  v-model="selectedProducers"
                  :value="producer"
                  density="compact"
                  hide-details
                  :color="color"
                  class="layer-tree__checkbox"
                />
              </template>
              <v-list-item-title class="layer-tree__label" style="overflow:hidden; text-overflow:ellipsis;">{{ producer }}</v-list-item-title>
              <template #append>
                <span :style="{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }" />
              </template>
            </v-list-item>
          </v-list-group>

          <!-- ── Géonotes ─────────────────────────────────────────── -->
          <v-list-group value="geonotes">
            <template #activator="{ props }">
              <v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header">
                <template #prepend>
                  <v-switch
                    v-model="showGéonotes"
                    density="compact"
                    hide-details
                    color="warning"
                    class="layer-tree__switch"
                    :disabled="géonotesData.length === 0"
                    @click.stop
                  />
                </template>
                <v-list-item-title class="layer-tree__group-title">Géonotes</v-list-item-title>
                <template #append>
                  <v-badge v-if="géonotesData.length" :content="géonotesData.length" color="warning" inline class="mr-1" />
                  <v-icon size="16" :icon="layerPanelGroups.includes('geonotes') ? 'M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z' : 'M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z'" style="opacity:0.6" />
                </template>
              </v-list-item>
            </template>

            <v-list-item density="compact" rounded="lg" disabled class="layer-tree__leaf">
              <template #prepend><v-icon icon="M19 7H16.19C15.74 6.2 15.12 5.5 14.37 5L16 3.41L14.59 2L12.42 4.17C11.96 4.06 11.5 4 11 4S10.05 4.06 9.59 4.17L7.41 2L6 3.41L7.62 5C6.87 5.5 6.26 6.21 5.81 7H3V9H5.09C5.03 9.33 5 9.66 5 10V11H3V13H5V14C5 14.34 5.03 14.67 5.09 15H3V17H5.81C7.26 19.5 10.28 20.61 13 19.65V19C13 18.43 13.09 17.86 13.25 17.31C12.59 17.76 11.8 18 11 18C8.79 18 7 16.21 7 14V10C7 7.79 8.79 6 11 6S15 7.79 15 10V14C15 14.19 15 14.39 14.95 14.58C15.54 14.04 16.24 13.62 17 13.35V13H19V11H17V10C17 9.66 16.97 9.33 16.91 9H19V7M21.34 16L17.75 19.59L16.16 18L15 19.16L17.75 22.16L22.5 17.41L21.34 16M13 9V11H9V9H13M13 13V15H9V13H13Z" size="13" class="mr-1" /></template>
              <v-list-item-title class="layer-tree__label">Dépistage</v-list-item-title>
              <template #append><v-chip size="x-small" label>Bientôt</v-chip></template>
            </v-list-item>
            <v-list-item density="compact" rounded="lg" disabled class="layer-tree__leaf">
              <template #prepend><v-icon icon="M15 3H5A2 2 0 0 0 3 5V19A2 2 0 0 0 5 21H19A2 2 0 0 0 21 19V9L15 3M19 19H5V5H14V10H19M17 14H7V12H17M14 17H7V15H14" size="13" class="mr-1" /></template>
              <v-list-item-title class="layer-tree__label">Notes personnelles</v-list-item-title>
              <template #append><v-chip size="x-small" label>Bientôt</v-chip></template>
            </v-list-item>
          </v-list-group>

          <!-- ── Capteurs ────────────────────────────────────────── -->
          <v-list-group value="capteurs">
            <template #activator="{ props }">
              <v-list-item v-bind="props" density="compact" rounded="lg" class="layer-tree__group-header">
                <template #prepend>
                  <v-switch
                    v-model="showCapteurs"
                    density="compact"
                    hide-details
                    color="primary"
                    class="layer-tree__switch"
                    disabled
                    @click.stop
                  />
                </template>
                <v-list-item-title class="layer-tree__group-title">Capteurs</v-list-item-title>
              </v-list-item>
            </template>

            <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon">
              <template #prepend><v-icon icon="M19,14.5C19,14.5 21,16.67 21,18A2,2 0 0,1 19,20A2,2 0 0,1 17,18C17,16.67 19,14.5 19,14.5M5,18V9A2,2 0 0,1 3,7A2,2 0 0,1 5,5V4A2,2 0 0,1 7,2H9A2,2 0 0,1 11,4V5H19A2,2 0 0,1 21,7V9L21,11A1,1 0 0,1 22,12A1,1 0 0,1 21,13H17A1,1 0 0,1 16,12A1,1 0 0,1 17,11V9H11V18H12A2,2 0 0,1 14,20V22H2V20A2,2 0 0,1 4,18H5Z" size="13" class="mr-1" /></template>
              <v-list-item-title class="layer-tree__label">Compteur d'eau</v-list-item-title>
              <template #append><v-btn size="x-small" variant="text" icon tag="a" href="https://www.agrimetrie.com/compteur-deau" target="_blank" @click.stop><v-icon size="13">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon></v-btn></template>
            </v-list-item>
            <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon">
              <template #prepend><v-icon icon="M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z" size="13" class="mr-1" /></template>
              <v-list-item-title class="layer-tree__label">Thermomètres</v-list-item-title>
              <template #append><v-btn size="x-small" variant="text" icon tag="a" href="https://www.agrimetrie.com/capteur-de-temperature-et-dhumidite" target="_blank" @click.stop><v-icon size="13">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon></v-btn></template>
            </v-list-item>
            <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon">
              <template #prepend><v-icon icon="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,14.4 19,16.5 17.3,18C15.9,16.7 14,16 12,16C10,16 8.2,16.7 6.7,18C5,16.5 4,14.4 4,12A8,8 0 0,1 12,4M14,5.89C13.62,5.9 13.26,6.15 13.1,6.54L11.81,9.77L11.71,10C11,10.13 10.41,10.6 10.14,11.26C9.73,12.29 10.23,13.45 11.26,13.86C12.29,14.27 13.45,13.77 13.86,12.74C14.12,12.08 14,11.32 13.57,10.76L13.67,10.5L14.96,7.29L14.97,7.26C15.17,6.75 14.92,6.17 14.41,5.96C14.28,5.91 14.15,5.89 14,5.89M10,6A1,1 0 0,0 9,7A1,1 0 0,0 10,8A1,1 0 0,0 11,7A1,1 0 0,0 10,6M7,9A1,1 0 0,0 6,10A1,1 0 0,0 7,11A1,1 0 0,0 8,10A1,1 0 0,0 7,9M17,9A1,1 0 0,0 16,10A1,1 0 0,0 17,11A1,1 0 0,0 18,10A1,1 0 0,0 17,9Z" size="13" class="mr-1" /></template>
              <v-list-item-title class="layer-tree__label">Tensiomètres</v-list-item-title>
              <template #append><v-btn size="x-small" variant="text" icon tag="a" href="https://www.agrimetrie.com/tensiometre" target="_blank" @click.stop><v-icon size="13">M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z</v-icon></v-btn></template>
            </v-list-item>
            <v-list-item density="compact" rounded="lg" class="layer-tree__leaf layer-tree__leaf--coming-soon">
              <template #prepend><v-icon icon="M18,15A3,3 0 0,1 21,18A3,3 0 0,1 18,21C16.69,21 15.58,20.17 15.17,19H14V17H15.17C15.58,15.83 16.69,15 18,15M18,17A1,1 0 0,0 17,18A1,1 0 0,0 18,19A1,1 0 0,0 19,18A1,1 0 0,0 18,17M18,8A1.43,1.43 0 0,0 19.43,6.57C19.43,5.78 18.79,5.14 18,5.14C17.21,5.14 16.57,5.78 16.57,6.57A1.43,1.43 0 0,0 18,8M18,2.57A4,4 0 0,1 22,6.57C22,9.56 18,14 18,14C18,14 14,9.56 14,6.57A4,4 0 0,1 18,2.57M8.83,17H10V19H8.83C8.42,20.17 7.31,21 6,21A3,3 0 0,1 3,18C3,16.69 3.83,15.58 5,15.17V14H7V15.17C7.85,15.47 8.53,16.15 8.83,17M6,17A1,1 0 0,0 5,18A1,1 0 0,0 6,19A1,1 0 0,0 7,18A1,1 0 0,0 6,17M6,3A3,3 0 0,1 9,6C9,7.31 8.17,8.42 7,8.83V10H5V8.83C3.83,8.42 3,7.31 3,6A3,3 0 0,1 6,3M6,5A1,1 0 0,0 5,6A1,1 0 0,0 6,7A1,1 0 0,0 7,6A1,1 0 0,0 6,5M11,19V17H13V19H11M7,13H5V11H7V13Z" size="13" class="mr-1" /></template>
              <v-list-item-title class="layer-tree__label">Suivi GPS</v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- ── Ma position ──────────────────────────────────────── -->
          <v-list-item
            density="compact"
            rounded="lg"
            :active="!!userLocation"
            color="primary"
            class="layer-tree__group-header"
            @click="userLocation ? (userLocation = null) : locateUser()"
          >
            <template #prepend>
              <v-icon
                icon="M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z"
                size="14"
                class="mr-1"
                :color="userLocation ? 'primary' : undefined"
              />
            </template>
            <v-list-item-title class="layer-tree__group-title">Ma position</v-list-item-title>
            <template #append>
              <v-progress-circular v-if="locating" indeterminate size="16" width="2" color="primary" />
              <v-icon v-else-if="userLocation" icon="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" size="14" color="primary" />
            </template>
          </v-list-item>
        </v-list>
      </div>
    </v-card>

    <!-- Radar status badge -->
    <transition name="radar-fade">
      <div v-if="showRadar" class="radar-badge" :class="{ 'radar-badge--unavail': !radarFrameLabel }">
        <v-icon size="13" style="margin-right:4px;">M19.07,4.93L17.66,6.34C19.1,7.79 20,9.79 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12C4,7.92 7.05,4.56 11,4.07V6.09C8.16,6.57 6,9.03 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12C18,10.34 17.33,8.84 16.24,7.76L14.83,9.17C15.55,9.9 16,10.9 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12C8,10.14 9.28,8.59 11,8.14V10.28C10.4,10.63 10,11.26 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12C14,11.26 13.6,10.62 13,10.28V2H12A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,9.24 20.88,6.74 19.07,4.93Z</v-icon>
        <span v-if="radarFrameLabel">Radar {{ radarFrameLabel }}</span>
        <span v-else>Radar – hors plage</span>
      </div>
    </transition>

    <!-- Bottom: Windy-style timeline bar -->
    <div class="timeline-bar">
      <div class="timeline-bar-inner">

        <!-- Play button -->
        <v-tooltip location="top" :text="isPlaying ? 'Pause' : 'Lecture'">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon size="small"
              :color="isPlaying ? 'primary' : undefined"
              :variant="isPlaying ? 'tonal' : 'text'"
              style="flex-shrink:0;"
              @click="togglePlay">
              <v-icon>{{ isPlaying ? 'M14,19H18V5H14M6,19H10V5H6V19Z' : 'M8,5.14V19.14L19,12.14L8,5.14Z' }}</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <!-- Date/time info + sub-actions -->
        <div class="timeline-info">
          <div class="timeline-time-main">
            <template v-if="!isRangeMode">{{ formatHourLabel(selectedHour) }}</template>
            <template v-else>{{ formatHourLabel(selectedHourRange[0]) }} – {{ formatHourLabel(selectedHourRange[1]) }}</template>
          </div>
          <div class="timeline-weekday">{{ formatWeekday(isRangeMode ? selectedDateTimeStart : selectedDateTime) }}</div>
          <div class="timeline-time-date">{{ formatDateLabel(isRangeMode ? selectedDateTimeStart : selectedDateTime) }}</div>
          <div class="timeline-sub-actions">
            <v-menu v-if="!showRadar" v-model="showTimelineDatePicker" location="top" :close-on-content-click="false">
              <template #activator="{ props }">
                <v-tooltip location="top" text="Choisir la date">
                  <template #activator="{ props: tooltipProps }">
                    <v-btn v-bind="{ ...props, ...tooltipProps }" icon size="x-small"
                      :color="showTimelineDatePicker ? 'primary' : undefined"
                      :variant="showTimelineDatePicker ? 'tonal' : 'text'">
                      <v-icon size="16">M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </template>
              <div class="pa-2">
                <DaySelector ref="daySelector" v-model="selectedDay" />
              </div>
            </v-menu>
            <v-tooltip v-if="!showRadar" location="top" :text="isRangeMode ? 'Heure unique' : 'Mode plage'">
              <template #activator="{ props }">
                <v-btn v-bind="props" icon size="x-small"
                  :color="isRangeMode ? 'primary' : undefined"
                  :variant="isRangeMode ? 'tonal' : 'text'"
                  @click="isRangeMode = !isRangeMode">
                  <v-icon size="16">{{ isRangeMode ? 'M4,9C5.31,9 6.42,9.83 6.83,11H17.17C17.58,9.83 18.69,9 20,9A3,3 0 0,1 23,12A3,3 0 0,1 20,15C18.69,15 17.58,14.17 17.17,13H6.83C6.42,14.17 5.31,15 4,15A3,3 0 0,1 1,12A3,3 0 0,1 4,9Z' : 'M2,11H9.17C9.58,9.83 10.69,9 12,9C13.31,9 14.42,9.83 14.83,11H22V13H14.83C14.42,14.17 13.31,15 12,15C10.69,15 9.58,14.17 9.17,13H2V11Z' }}</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </div>

        <!-- Track -->
        <div class="timeline-track-area" ref="timelineRef" @pointerdown="onTrackPointerDown">

          <!-- Radar coverage stripe -->
          <div
            v-if="showRadar && radarExtent"
            class="timeline-radar-stripe"
            :style="{ left: hourToPct(radarExtent.min) + '%', width: Math.max(0, hourToPct(radarExtent.max) - hourToPct(radarExtent.min)) + '%' }"
          />
          <!-- Range fill -->
          <div
            v-if="isRangeMode"
            class="timeline-range-fill"
            :style="{ left: hourToPct(selectedHourRange[0]) + '%', width: (hourToPct(selectedHourRange[1]) - hourToPct(selectedHourRange[0])) + '%' }"
          />
          <div
            v-for="tick in timelineTicks" :key="tick.h"
            class="timeline-tick"
            :class="tick.major ? 'timeline-tick--major' : 'timeline-tick--minor'"
            :style="{ left: tick.pct + '%' }"
          >
            <div class="timeline-tick-line" />
            <div class="timeline-tick-label">{{ tick.label }}</div>
          </div>
          <div v-if="!isRangeMode" class="timeline-playhead" :style="{ left: hourToPct(selectedHour) + '%' }" />
          <template v-else>
            <div class="timeline-playhead timeline-playhead--range" :style="{ left: hourToPct(selectedHourRange[0]) + '%' }" />
            <div class="timeline-playhead timeline-playhead--range" :style="{ left: hourToPct(selectedHourRange[1]) + '%' }" />
          </template>
          <div v-if="nowHour !== null" class="timeline-now" :style="{ left: hourToPct(nowHour) + '%' }">
            <div class="timeline-now-line" />
            <div class="timeline-now-label">maintenant</div>
          </div>
        </div>

      </div>
    </div>

    <v-dialog v-model="dialogVisible" max-width="800">
      <v-card v-if="selectedSource">
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">M12,5A7,7 0 0,0 5,12H7A5,5 0 0,1 12,7A5,5 0 0,1 17,12H19A7,7 0 0,0 12,5M13,14.29C13.88,13.9 14.5,13.03 14.5,12A2.5,2.5 0 0,0 12,9.5A2.5,2.5 0 0,0 9.5,12C9.5,13 10.12,13.9 11,14.29V17.59L7.59,21L9,22.41L12,19.41L15,22.41L16.41,21L13,17.59V14.29M12,1A11,11 0 0,0 1,12H3A9,9 0 0,1 12,3A9,9 0 0,1 21,12H23A11,11 0 0,0 12,1Z</v-icon>
          {{ selectedSource.properties.name }}
        </v-card-title>
        <v-card-text>
          <VChart :option="chartOptions" autoresize style="height: 400px; width: 100%;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="dialogVisible = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Field note dialog ──────────────────────────────────────────────── -->
    <v-dialog v-model="showNoteDialog" max-width="480" scrollable>
      <v-card :style="{ background: panelBg, backdropFilter: 'blur(12px)' }" elevation="12">
        <v-card-title class="d-flex align-center gap-2 pt-4 pb-2 px-4">
          <v-icon color="warning">M5 19V5H12V12H19V13C19.7 13 20.37 13.13 21 13.35V9L15 3H5C3.89 3 3 3.89 3 5V19C3 20.1 3.89 21 5 21H13.35C13.13 20.37 13 19.7 13 19H5M14 4.5L19.5 10H14V4.5M23 18V20H20V23H18V20H15V18H18V15H20V18H23Z</v-icon>
          <span :style="{ color: panelTextMain }">Nouvelle géonote</span>
          <v-spacer />
          <v-btn icon size="small" variant="text" @click="showNoteDialog = false">
            <v-icon>M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-row dense>
            <!-- DATE -->
            <v-col cols="12">
              <v-text-field
                v-model="noteForm.date"
                label="Date"
                type="date"
                density="compact"
                variant="outlined"
                prepend-inner-icon="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"
                hide-details="auto"
              />
            </v-col>

            <!-- FERME -->
            <v-col cols="12">
              <v-text-field
                v-model="noteForm.ferme"
                label="Ferme"
                density="compact"
                variant="outlined"
                prepend-inner-icon="M12 5.69L17 10.19V18H15V12H9V18H7V10.19L12 5.69M12 3L2 12H5V20H11V14H13V20H19V12H22"
                :placeholder="userLocation ? 'Détecté automatiquement' : 'Position non disponible'"
                hide-details="auto"
              />
            </v-col>

            <!-- CHAMP -->
            <v-col cols="12">
              <v-text-field
                v-model="noteForm.champ"
                label="Champ"
                density="compact"
                variant="outlined"
                prepend-inner-icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z"
                :placeholder="userLocation ? 'Détecté automatiquement' : 'Position non disponible'"
                hide-details="auto"
              />
            </v-col>

            <!-- NOTE -->
            <v-col cols="12">
              <v-textarea
                v-model="noteForm.note"
                label="Note"
                density="compact"
                variant="outlined"
                prepend-inner-icon="M21,6V8H3V6H21M3,18H12V16H3V18M3,13H21V11H3V13Z"
                rows="4"
                auto-grow
                hide-details="auto"
                placeholder="Décrivez votre observation..."
              />
            </v-col>

            <!-- PHOTO -->
            <v-col cols="12">
              <div class="note-photo-field">
                <label class="note-photo-label">
                  <v-icon size="18" class="mr-1">M20,4H16.83L15,2H9L7.17,4H4A2,2 0 0,0 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6A2,2 0 0,0 20,4M20,18H4V6H8.05L9.88,4H14.12L15.95,6H20V18M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15Z</v-icon>
                  Photo (optionnelle)
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    class="note-photo-input"
                    @change="onNotePhotoChange"
                  />
                </label>
                <v-img
                  v-if="notePhotoPreview"
                  :src="notePhotoPreview"
                  max-height="160"
                  contain
                  class="mt-2 rounded-lg"
                />
              </div>
            </v-col>

            <!-- GPS coords indicator -->
            <v-col v-if="noteCoords" cols="12">
              <div class="note-gps-chip">
                <v-icon size="14" class="mr-1" color="primary">M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M3.05,13H1V11H3.05C3.5,6.83 6.83,3.5 11,3.05V1H13V3.05C17.17,3.5 20.5,6.83 20.95,11H23V13H20.95C20.5,17.17 17.17,20.5 13,20.95V23H11V20.95C6.83,20.5 3.5,17.17 3.05,13M12,5A7,7 0 0,0 5,12A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z</v-icon>
                <span>{{ noteCoords[1].toFixed(6) }}, {{ noteCoords[0].toFixed(6) }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn variant="text" @click="showNoteDialog = false">Annuler</v-btn>
          <v-btn
            color="warning"
            variant="tonal"
            :loading="noteSubmitting"
            :disabled="!noteForm.note.trim()"
            @click="submitNote"
          >
            <v-icon start>M2,21L23,12L2,3V10L17,12L2,14V21Z</v-icon>
            Envoyer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.controls-overlay-right {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  backdrop-filter: blur(10px);
  width: 200px;
}

/* ── Mobile layer FAB ─────────────────────────────────────────────────────── */
.layer-fab {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

/* ── Layer tree ───────────────────────────────────────────────────────────── */
.layer-tree {
  padding: 2px 4px !important;
}

.layer-tree :deep(.v-list-group__items .v-list-item) {
  min-height: 24px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.layer-tree__group-header {
  min-height: 28px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.layer-tree__group-title {
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
}

.layer-tree__label {
  font-size: 11px !important;
  line-height: 1.2 !important;
}

.layer-tree__leaf {
  padding-left: 20px !important;
  min-height: 24px !important;
}

.layer-tree__leaf--coming-soon {
  opacity: 0.55;
  cursor: default;
  pointer-events: none;
}
.layer-tree__leaf--coming-soon .v-btn {
  pointer-events: auto;
  opacity: 1;
}

.layer-tree__producer {
  padding-left: 12px !important;
}

.layer-tree__switch {
  flex-shrink: 0;
  margin-right: 8px;
}

.layer-tree__switch :deep(.v-switch__track) {
  height: 12px !important;
  width: 24px !important;
}

.layer-tree__switch :deep(.v-switch__thumb) {
  width: 10px !important;
  height: 10px !important;
}

.layer-tree__checkbox :deep(.v-checkbox-btn) {
  min-height: 20px !important;
}

.layer-tree__all-none {
  display: flex;
  gap: 2px;
  padding: 0 4px 2px 28px;
}

.layer-tree__subheader {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 4px 4px 0 28px;
}

.layer-tree__select-wrap {
  padding: 4px 12px 6px 28px;
}

.layer-tree__select {
  font-size: 12px;
}

.layer-tree__select :deep(.v-field) {
  border-radius: 10px;
}

.layer-tree__select-summary {
  font-size: 12px;
  font-weight: 500;
}

/* ── Timeline bar ─────────────────────────────────────────────────────────── */
.timeline-bar {
  position: absolute;
  bottom: 20px;
  left: 12px;
  right: 12px;
  z-index: 10;
  pointer-events: none;
}

.timeline-bar-inner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 14px;
  background: v-bind(panelBg);
  backdrop-filter: blur(10px);
  box-shadow: v-bind(panelShadow);
  pointer-events: all;
}

.timeline-info {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  min-width: 88px;
}

.timeline-sub-actions {
  display: flex;
  gap: 0;
  margin-top: 1px;
  margin-left: -4px;
}

.timeline-time-main {
  font-size: 15px;
  font-weight: 700;
  color: v-bind(panelTextMain);
  white-space: nowrap;
}

.timeline-weekday {
  font-size: 9px;
  font-weight: 600;
  color: v-bind(panelTextMain);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.75;
  margin-top: 2px;
}

.timeline-time-date {
  font-size: 10px;
  color: v-bind(panelTextSub);
  white-space: nowrap;
  text-transform: capitalize;
}

.timeline-track-area {
  flex: 1;
  position: relative;
  height: 42px;
  cursor: pointer;
  user-select: none;
  overflow: visible;
}

.timeline-track-area::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  transform: translateY(-50%);
  background: v-bind(panelTrackBg);
  border-radius: 1px;
}

.timeline-range-fill {
  position: absolute;
  top: 50%;
  height: 4px;
  transform: translateY(-50%);
  background: v-bind(panelRangeFill);
  border-radius: 2px;
  pointer-events: none;
}

.timeline-tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  pointer-events: none;
}

.timeline-tick-line {
  position: absolute;
  left: 0;
  width: 1.5px;
  transform: translateX(-50%);
  border-radius: 1px;
}

.timeline-tick--minor .timeline-tick-line {
  height: 6px;
  top: calc(50% - 3px);
  background: v-bind(panelTickMinor);
}

.timeline-tick--major .timeline-tick-line {
  height: 12px;
  top: calc(50% - 6px);
  background: v-bind(panelTickMajor);
}

.timeline-tick-label {
  position: absolute;
  left: 0;
  transform: translateX(-50%);
  white-space: nowrap;
}

.timeline-tick--minor .timeline-tick-label {
  bottom: calc(50% + 5px);
  top: auto;
  font-size: 9px;
  color: v-bind(panelLabelMinor);
}

.timeline-tick--major .timeline-tick-label {
  top: calc(50% + 8px);
  font-size: 10px;
  font-weight: 600;
  color: v-bind(panelLabelMajor);
}

.timeline-playhead {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: v-bind(panelTextMain);
  box-shadow: 0 0 0 2px rgb(var(--v-theme-primary)), 0 2px 8px rgba(0, 0, 0, 0.5);
  pointer-events: none;
  transition: left 0.06s linear;
}

.timeline-playhead--range {
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 2px v-bind(panelTextMain), 0 2px 8px rgba(0, 0, 0, 0.5);
}
/* ────────────────────────────────────────────────────────────────────────── */

@media (max-width: 600px) {
  .timeline-tick-label { display: none; }
  .timeline-track-area { height: 24px; }
}

.timeline-now {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  pointer-events: none;
  z-index: 2;
}

.timeline-now-line {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  transform: translateX(-50%);
  background: rgb(var(--v-theme-error, 244 67 54));
  border-radius: 1px;
  opacity: 0.9;
}

.timeline-now-label {
  position: absolute;
  left: 0;
  top: auto;
  bottom: calc(100% + 3px);
  transform: translateX(-50%);
  font-size: 8px;
  font-weight: 700;
  color: rgb(var(--v-theme-error, 244 67 54));
  white-space: nowrap;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* ── Custom map marker ───────────────────────────────────────────────────── */

/* Wrapper: Mapbox anchors its bottom-center to the coordinate,
   so the pin tip (last child) lands exactly on the geo-point. */
/* ─────────────────────────────────────────────────────────────────────────── */

.custom-marker-wrap {
  --accent: rgb(var(--v-theme-primary));
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.custom-marker-wrap--hidden {
  opacity: 0 !important;
  transform: scale(0.25) !important;
  pointer-events: none !important;
}
.custom-marker-wrap:hover .custom-marker {
  box-shadow: 0 4px 20px rgba(0,0,0,0.45), 0 0 0 2px var(--accent);
  transform: translateY(-1px);
}

.custom-marker {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: v-bind(panelBg);
  border: 2.5px solid var(--accent);
  border-radius: 10px;
  padding: 5px 9px 4px;
  min-width: 64px;
  max-width: 90px;
  color: v-bind(panelTextMain);
  text-align: center;
  box-shadow: 0 3px 14px rgba(0,0,0,0.38), 0 0 0 0 var(--accent);
  backdrop-filter: blur(14px);
  transition: box-shadow 0.15s, transform 0.15s, max-width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1);
}

/* Expanded state — lift above sibling markers */
.custom-marker-wrap--expanded {
  position: relative;
  z-index: 100;
}

/* Expanded state — widen the box to fit the details panel */
.custom-marker-wrap--expanded .custom-marker {
  min-width: 248px;
  max-width: 280px;
  box-shadow: 0 6px 28px rgba(0,0,0,0.50), 0 0 0 2px var(--accent);
  transform: translateY(-2px);
}

/* ── Inline details (collapsed by default) ─────────────────────────────── */
.custom-marker__details {
  width: 100%;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition:
    max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity    0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 0;
}

.custom-marker-wrap--expanded .custom-marker__details {
  max-height: 320px;
  opacity: 1;
  margin-top: 4px;
}

.custom-marker__details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 4px 4px;
}

.custom-marker__details-title {
  font-size: 13px;
  font-weight: 700;
  color: v-bind(panelTextMain);
  line-height: 1.25;
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* downward pin tip — normal flow so wrapper height includes the tip */
.custom-marker__pin {
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 8px solid var(--accent);
  pointer-events: none;
}

.custom-marker__name {
  font-size: 9px;
  font-weight: 600;
  color: v-bind(panelTextSub);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  max-width: 80px;
  text-overflow: ellipsis;
  line-height: 1.1;
  margin-bottom: 1px;
}

.custom-marker__value {
  font-size: 15px;
  font-weight: 800;
  color: v-bind(panelTextMain);
  line-height: 1.15;
  white-space: nowrap;
  letter-spacing: -0.01em;
}
.custom-marker__dash  { color: v-bind(panelTextSub); font-weight: 400; font-size: 16px; }
.custom-marker__risk  { color: rgb(220,38,38); font-size: 16px; }
.custom-marker__ok    { color: rgb(34,197,94);  font-size: 15px; }

.custom-marker__spark {
  display: block;
  margin-top: 3px;
  border-radius: 2px;
  overflow: visible;
}
.custom-marker__spark-placeholder {
  height: 4px;
  margin-top: 3px;
}

/* tiny dot in top-right corner when showing forecast data */
.custom-marker__forecast-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--v-theme-warning));
  box-shadow: 0 0 0 1.5px v-bind(panelBg);
}

/* Remove unused popup tip overrides (no longer using mapbox popup) */
/* ── Mapbox popup chrome overrides ────────────────────────────────────────── */
:deep(.mapboxgl-popup-content) {
  background: v-bind(panelBg) !important;
  box-shadow: v-bind(panelShadow) !important;
  border-radius: 12px !important;
  padding: 0 !important;
  backdrop-filter: blur(14px);
  border: 1px solid v-bind(panelTickMinor);
  overflow: hidden;
  min-width: 0;
}

:deep(.mapboxgl-popup-tip) {
  border-top-color: v-bind(panelBg) !important;
  border-bottom-color: v-bind(panelBg) !important;
  border-left-color: v-bind(panelBg) !important;
  border-right-color: v-bind(panelBg) !important;
}

:deep(.mapboxgl-popup-close-button) {
  color: v-bind(panelTextSub) !important;
  font-size: 20px !important;
  line-height: 1;
  padding: 4px 8px !important;
  border-radius: 0 12px 0 0;
  transition: color 0.15s, background 0.15s;
  z-index: 1;
}
:deep(.mapboxgl-popup-close-button:hover) {
  color: v-bind(panelTextMain) !important;
  background: v-bind(panelRangeFill) !important;
}

/* ── Marker popup card ────────────────────────────────────────────────────── */
.marker-popup {
  min-width: 248px;
  font-family: inherit;
  color: v-bind(panelTextMain);
}

.marker-popup__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px 8px;
  padding-right: 32px; /* leave room for mapbox close button */
}

.marker-popup__title {
  font-size: 14px;
  font-weight: 700;
  color: v-bind(panelTextMain);
  line-height: 1.25;
  flex: 1;
}

.marker-popup__open-btn {
  opacity: 0.55;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.marker-popup__open-btn:hover {
  opacity: 1;
}

.marker-popup__forecast-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 12px 8px;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: rgba(var(--v-theme-warning), 0.15);
  color: rgb(var(--v-theme-warning));
}

.marker-popup__divider {
  height: 1px;
  background: v-bind(panelTickMinor);
}

.marker-popup__metrics {
  padding: 4px 0 6px;
}

.marker-popup__row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  transition: background 0.15s;
  border-radius: 0;
}
.marker-popup__row.active {
  background: v-bind(panelRangeFill);
}

.marker-popup__icon {
  color: v-bind(panelTextSub) !important;
  flex-shrink: 0;
  transition: color 0.15s;
}
.marker-popup__row.active .marker-popup__icon {
  color: rgb(var(--v-theme-primary)) !important;
}

.marker-popup__label {
  flex: 1;
  font-size: 12px;
  color: v-bind(panelTextSub);
  white-space: nowrap;
  transition: color 0.15s;
}
.marker-popup__row.active .marker-popup__label {
  color: v-bind(panelTextMain);
  font-weight: 600;
}

.marker-popup__value {
  font-size: 13px;
  font-weight: 600;
  color: v-bind(panelTextMain);
  white-space: nowrap;
  text-align: right;
}

.marker-popup__subvalue {
  font-size: 10px;
  font-weight: 400;
  color: v-bind(panelTextSub);
}

/* ── Radar coverage stripe on timeline ───────────────────────────────────── */
.timeline-radar-stripe {
  position: absolute;
  top: 50%;
  height: 6px;
  transform: translateY(-50%);
  background: rgba(99, 179, 237, 0.28);
  border: 1px solid rgba(99, 179, 237, 0.55);
  border-radius: 3px;
  pointer-events: none;
}
/* ─────────────────────────────────────────────────────────────────────────── */

/* ── Radar badge ──────────────────────────────────────────────────────────── */
.radar-badge {
  position: absolute;
  bottom: 88px;
  left: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 8px;
  background: v-bind(panelBg);
  backdrop-filter: blur(10px);
  box-shadow: v-bind(panelShadow);
  font-size: 11px;
  font-weight: 600;
  color: v-bind(panelTextMain);
  pointer-events: none;
  border: 1px solid v-bind(panelTickMinor);
}

.radar-badge--unavail {
  opacity: 0.55;
}

.radar-fade-enter-active,
.radar-fade-leave-active {
  transition: opacity 0.25s ease;
}
.radar-fade-enter-from,
.radar-fade-leave-to {
  opacity: 0;
}
/* ─────────────────────────────────────────────────────────────────────────── */

/* ── User GPS location marker ────────────────────────────────────────────── */
.user-location-marker {
  position: relative;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Géonote map pin ─────────────────────────────────────────────────────── */
.spore-capteur-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Override Mapbox's latitude-based z-index on the marker container */
:global(.mapboxgl-marker:has(.custom-marker-wrap)) {
  z-index: 10 !important;
}
:global(.mapboxgl-marker:has(.spore-capteur-wrap)) {
  z-index: 10 !important;
}

.spore-capteur-wrap--hidden {
  opacity: 0 !important;
  transform: scale(0.25) !important;
  pointer-events: none !important;
}

.spore-capteur-wrap--expanded {
  position: relative;
  z-index: 100;
}

.spore-capteur-box {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: v-bind(panelBg);
  border: 2px solid var(--spore-color, #E65100);
  border-radius: 8px;
  padding: 4px 6px 3px;
  min-width: 30px;
  max-width: 36px;
  color: v-bind(panelTextMain);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.40);
  backdrop-filter: blur(14px);
  transition: box-shadow 0.15s, transform 0.15s, min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.spore-capteur-wrap:hover .spore-capteur-box {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.45), 0 0 0 2px var(--spore-color, #E65100);
  transform: translateY(-1px);
}

.spore-capteur-wrap--expanded .spore-capteur-box {
  min-width: 200px;
  max-width: 240px;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.5), 0 0 0 2px var(--spore-color, #E65100);
  transform: translateY(-2px);
}

.spore-capteur-box__icon {
  color: var(--spore-color, #E65100) !important;
  -webkit-text-stroke: 0.5px var(--spore-stroke, #7B2D00);
}

.spore-capteur-wrap:hover .spore-capteur-box__icon {
  transform: scale(1.1);
  transition: transform 0.15s;
}

.spore-capteur-details {
  width: 100%;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition:
    max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity    0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 0;
}

.spore-capteur-wrap--expanded .spore-capteur-details {
  max-height: 320px;
  overflow-y: auto;
  opacity: 1;
  margin-top: 4px;
}

.spore-capteur-details__name {
  font-size: 13px;
  font-weight: 700;
  color: v-bind(panelTextMain);
  white-space: normal;
  word-break: break-word;
  max-width: 190px;
  padding: 4px 2px 0;
}

.spore-capteur-details__id {
  font-size: 11px;
  font-weight: 500;
  color: v-bind(panelTextSub);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;
  padding: 0 2px 4px;
}

.spore-capteur-details__row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: v-bind(panelTextSub);
  padding: 2px 4px;
  white-space: nowrap;
}

.spore-capteur-mesures__group {
  padding: 2px 4px;
}

.spore-capteur-mesures__date {
  font-size: 11px;
  font-weight: 700;
  color: v-bind(panelTextMain);
  text-transform: capitalize;
}

.spore-capteur-mesures__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: v-bind(panelTextSub);
  padding: 1px 0 1px 8px;
}

.spore-capteur-mesures__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spore-capteur-mesures__culture {
  font-size: 10px;
  font-weight: 600;
  color: v-bind(panelTextSub);
  opacity: 0.85;
}

.spore-capteur-mesures__culture::before {
  content: '· ';
}

.spore-capteur-mesures__val {
  font-weight: 700;
  color: v-bind(panelTextMain);
}

.spore-capteur-mesures__empty {
  font-style: italic;
  opacity: 0.8;
}

.spore-capteur-pin {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid var(--spore-color, #E65100);
  pointer-events: none;
}

.geonote-pin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px 8px 2px 8px;
  background: rgb(var(--v-theme-warning));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.40);
  cursor: pointer;
  transform: rotate(-4deg);
  transition: transform 0.15s, box-shadow 0.15s;
}

.geonote-pin:hover {
  transform: rotate(0deg) scale(1.12);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.50);
}

.geonote-pin--hidden {
  opacity: 0 !important;
  transform: scale(0.25) !important;
  pointer-events: none !important;
}

.geonote-pin__icon {
  color: #fff !important;
  opacity: 0.95;
}

/* Mapbox popup used by géonote - override default white box */
.geonote-popup {
  min-width: 180px;
  max-width: 260px;
  font-family: inherit;
  font-size: 13px;
}

.geonote-popup__header {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
}

.geonote-popup__title {
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgb(var(--v-theme-warning));
  flex: 1;
}

.geonote-popup__date {
  font-size: 11px;
  opacity: 0.60;
}

.geonote-popup__meta {
  font-size: 11px;
  opacity: 0.65;
  margin-bottom: 5px;
}

.geonote-popup__note {
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.geonote-popup__photo {
  width: 100%;
  max-height: 140px;
  object-fit: cover;
  border-radius: 6px;
  margin-top: 8px;
}
/* ─────────────────────────────────────────────────────────────────────────── */

/* ── Field note dialog ───────────────────────────────────────────────────── */
.note-photo-field {
  display: flex;
  flex-direction: column;
}

.note-photo-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1.5px dashed rgba(128, 128, 128, 0.45);
  font-size: 13px;
  transition: border-color 0.18s, background 0.18s;
  user-select: none;
}

.note-photo-label:hover {
  border-color: rgb(var(--v-theme-warning));
  background: rgba(var(--v-theme-warning), 0.07);
}

.note-photo-input {
  display: none;
}

.note-gps-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: rgba(128, 128, 128, 0.75);
  padding: 2px 8px;
  border-radius: 99px;
  background: rgba(128, 128, 128, 0.10);
  width: fit-content;
}
/* ─────────────────────────────────────────────────────────────────────────── */

.user-location-marker__dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  border: 2.5px solid #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.35);
  position: relative;
  z-index: 1;
}

.user-location-marker__pulse {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  opacity: 0.35;
  animation: user-location-pulse 2s ease-out infinite;
}

@keyframes user-location-pulse {
  0%   { transform: scale(1);   opacity: 0.35; }
  70%  { transform: scale(2.8); opacity: 0; }
  100% { transform: scale(2.8); opacity: 0; }
}
/* ─────────────────────────────────────────────────────────────────────────── */
</style>
